import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  InputBase,
  Menu,
  MenuItem,
  Grid,
  Skeleton,
  Alert,
  Dialog,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Public as PublicIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocalOffer as TagIcon,
  CollectionsBookmark as CollectionIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Language as LanguageIcon,
  Newspaper as NewspaperIcon,
  Tv as TvIcon,
  Radio as RadioIcon,
  Podcasts as PodcastIcon,
  X as XIcon,
  Label as LabelIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  CalendarToday as CalendarTodayIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Tune as TuneIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ContentCopy as ContentCopyIcon,
  Bolt as BoltIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useArticlesQuery } from '../hooks/data/useArticlesQuery';
import { useBrand } from '../context/BrandContext';
import { API_CONFIG } from '../config/api';
import api from '../services/api';
import { getYouTubeId } from '../utils/contentProcessor';
import LiveResultsReport from '../components/organisms/LiveResultsReport';
import VisualFilterExplorer from '../components/organisms/VisualFilterExplorer';

const fallbackImage = 'https://media.mensuremidia.com.br/system/fallbacks/mensure-thumbnail.png';

const addProxyIfNeeded = (url) => {
  if (!url) return url;
  if (url.includes('/api/proxy/')) return url;

  const needsProxy = url.includes('fbcdn.net')
    || url.includes('cdninstagram.com')
    || url.includes('instagram.com')
    || url.includes('scontent');

  return needsProxy
    ? `${API_CONFIG.BASE_URL}/proxy/instagram-image?url=${encodeURIComponent(url)}`
    : url;
};

const getImageUrl = (article) => {
  const isPrintMedia = article?.mediaType === 'Jornal' || article?.mediaType === 'Revista';
  const src = (isPrintMedia && article?.thumbnail)
    ? article.thumbnail
    : article?.imageUrl || article?.image || article?.thumbnail || fallbackImage;

  return addProxyIfNeeded(src) || fallbackImage;
};

const formatCompact = (value) => {
  const number = Number(value || 0);
  if (!number) return '0';
  if (number >= 1000000) return `${(number / 1000000).toFixed(1).replace('.0', '')}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(1).replace('.0', '')}k`;
  return number.toLocaleString('pt-BR');
};

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (!number) return null;
  if (number >= 1000000) return `R$ ${(number / 1000000).toFixed(1).replace('.0', '')}M`;
  if (number >= 1000) return `R$ ${(number / 1000).toFixed(1).replace('.0', '')}k`;
  return `R$ ${number.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
};

const formatDate = (value) => {
  if (!value) return 'Sem data';
  try {
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC'
    });
  } catch {
    return 'Sem data';
  }
};

const cleanArticleText = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value
      .map(block => cleanArticleText(block?.content || block?.text || block))
      .filter(Boolean)
      .join('\n\n');
  }

  return String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getModalYouTubeId = (article) => {
  if (!article) return null;
  return article.videoId
    || article.youtubeId
    || getYouTubeId(article.videoUrl)
    || getYouTubeId(article.siteUrl)
    || getYouTubeId(article.url);
};

const getPlayableVideoUrl = (article) => {
  if (!article?.videoUrl || getModalYouTubeId(article)) return null;
  return article.videoUrl;
};

const normalizeRelationLabel = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value.name || value.title || value.label || value.slug || value._id || value.id || null;
};

const getArticleTags = (article) => (
  Array.isArray(article?.tags)
    ? article.tags.map(normalizeRelationLabel).filter(Boolean)
    : []
);

const getArticleCollections = (article) => {
  const collections = article?.collections || article?.collectionIds || article?.collection || [];
  return (Array.isArray(collections) ? collections : [collections])
    .map(normalizeRelationLabel)
    .filter(Boolean);
};

const PlatformIcon = ({ mediaType }) => {
  switch (mediaType) {
    case 'YouTube':
      return <YouTubeIcon />;
    case 'Instagram':
      return <InstagramIcon />;
    case 'Facebook':
      return <FacebookIcon />;
    case 'LinkedIn':
      return <LinkedInIcon />;
    case 'Twitter':
      return <XIcon />;
    case 'Jornal':
    case 'Revista':
      return <NewspaperIcon />;
    case 'TV':
      return <TvIcon />;
    case 'Radio':
      return <RadioIcon />;
    case 'Podcast':
      return <PodcastIcon />;
    default:
      return <LanguageIcon />;
  }
};

const getUniqueValues = (items, mapper, fallback = []) => {
  const values = items
    .flatMap(item => {
      const value = mapper(item);
      return Array.isArray(value) ? value : [value];
    })
    .map(value => (value ? String(value).trim() : ''))
    .filter(Boolean);

  return Array.from(new Set(values)).slice(0, 8).length
    ? Array.from(new Set(values)).slice(0, 8)
    : fallback;
};

const ConceptChip = ({ label, active = false }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 32,
      borderRadius: '10px',
      bgcolor: active ? '#f8fafc' : 'rgba(255,255,255,0.06)',
      color: active ? '#111827' : 'rgba(248,250,252,0.82)',
      border: `1px solid ${active ? 'rgba(248,250,252,0.86)' : 'rgba(255,255,255,0.09)'}`,
      fontWeight: 750,
      '& .MuiChip-label': { px: 1.15 }
    }}
  />
);

const ToolbarSearchConcept = ({ articles, brandName }) => {
  const [searchOpen, setSearchOpen] = React.useState(true);
  const [viewMode, setViewMode] = React.useState('list');
  const tags = getUniqueValues(articles, getArticleTags, ['softex', 'ia', 'governo', 'educacao', 'inovacao', 'crise']);
  const sources = getUniqueValues(articles, article => article.siteName || article.author, ['Agencia Brasil', 'Estadao', 'CNN', 'LinkedIn']);
  const quickFilters = ['Nao lidos', 'Favoritos', 'Com imagem', 'Sem tags', 'Ultima semana'];
  const advancedFilters = ['Data', 'Plataforma', 'Formato', 'Tier', 'Fonte', 'Sentimento', 'Status', 'Tags', 'Colecoes'];

  return (
    <Box
      sx={{
        mb: 4,
        borderRadius: '18px',
        overflow: 'hidden',
        bgcolor: '#101216',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 18px 34px -26px rgba(0,0,0,0.8)'
      }}
    >
      <Box
        sx={{
          minHeight: 72,
          px: { xs: 1.5, md: 2 },
          py: 1.35,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          borderBottom: searchOpen ? '1px solid rgba(255,255,255,0.07)' : 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0, flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: '#f8fafc',
              color: '#111827',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 950
            }}
          >
            M
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 900, lineHeight: 1.05 }}>
              Artigos
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.56)', fontWeight: 750 }}>
              {articles.length || 60} de 537 publicacoes
            </Typography>
          </Box>
        </Box>

        <Button
          onClick={() => setSearchOpen(open => !open)}
          startIcon={<SearchIcon />}
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 auto' },
            minWidth: { xs: '100%', md: 360 },
            maxWidth: { md: 560 },
            height: 44,
            justifyContent: 'flex-start',
            px: 1.6,
            borderRadius: '12px',
            bgcolor: searchOpen ? 'rgba(248,250,252,0.12)' : 'rgba(255,255,255,0.055)',
            color: 'rgba(248,250,252,0.78)',
            border: `1px solid ${searchOpen ? 'rgba(96,165,250,0.62)' : 'rgba(255,255,255,0.08)'}`,
            textTransform: 'none',
            fontWeight: 750,
            '&:hover': { bgcolor: 'rgba(248,250,252,0.14)' }
          }}
        >
          Buscar artigos, fontes, tags...
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, ml: { md: 'auto' }, flexWrap: 'wrap' }}>
          <Button
            startIcon={<CalendarTodayIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              height: 42,
              px: 1.5,
              borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.08)',
              color: '#f8fafc',
              border: '1px solid rgba(255,255,255,0.08)',
              textTransform: 'none',
              fontWeight: 850
            }}
          >
            Todos
          </Button>
          <IconButton
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.08)',
              color: '#f8fafc',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', p: 0.35, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <IconButton
              onClick={() => setViewMode('list')}
              sx={{ width: 34, height: 34, borderRadius: '9px', color: viewMode === 'list' ? '#111827' : 'rgba(248,250,252,0.72)', bgcolor: viewMode === 'list' ? '#f8fafc' : 'transparent' }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('grid')}
              sx={{ width: 34, height: 34, borderRadius: '9px', color: viewMode === 'grid' ? '#111827' : 'rgba(248,250,252,0.72)', bgcolor: viewMode === 'grid' ? '#f8fafc' : 'transparent' }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          </Box>
          <Button
            startIcon={<AddIcon />}
            sx={{
              height: 42,
              px: 1.8,
              borderRadius: '999px',
              bgcolor: '#f8fafc',
              color: '#111827',
              textTransform: 'none',
              fontWeight: 950,
              '&:hover': { bgcolor: '#e5e7eb' }
            }}
          >
            Artigo
          </Button>
          <Button
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              height: 42,
              px: 1.35,
              borderRadius: '999px',
              bgcolor: 'rgba(255,255,255,0.08)',
              color: '#f8fafc',
              textTransform: 'none',
              fontWeight: 850
            }}
          >
            {brandName || 'Softex'}
          </Button>
          <IconButton sx={{ width: 42, height: 42, borderRadius: '12px', color: '#f8fafc', bgcolor: 'rgba(255,255,255,0.08)' }}>
            <AdminPanelSettingsIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {searchOpen && (
        <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2 }}>
          <Box
            sx={{
              mx: 'auto',
              maxWidth: 1040,
              borderRadius: '16px',
              bgcolor: '#1f2227',
              border: '1px solid rgba(255,255,255,0.08)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ px: 1.5, py: 1.1, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <SearchIcon sx={{ color: 'rgba(248,250,252,0.56)', fontSize: 22 }} />
              <InputBase
                fullWidth
                placeholder="Buscar por titulo, veiculo, tag ou palavra-chave"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 750,
                  '& input::placeholder': { color: 'rgba(248,250,252,0.48)', opacity: 1 }
                }}
              />
              <Box sx={{ px: 1, height: 26, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(248,250,252,0.52)', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', fontSize: 12, fontWeight: 850 }}>
                Ctrl K
              </Box>
            </Box>

            <Box sx={{ p: { xs: 1.5, md: 2 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' }, gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" sx={{ color: 'rgba(248,250,252,0.48)', fontWeight: 900, letterSpacing: 0 }}>
                  Filtros rapidos
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.6, mb: 2 }}>
                  {quickFilters.map((filter, index) => (
                    <ConceptChip label={filter} active={index === 0} key={filter} />
                  ))}
                </Box>

                <Typography variant="overline" sx={{ color: 'rgba(248,250,252,0.48)', fontWeight: 900, letterSpacing: 0 }}>
                  Refinar busca
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1, mt: 0.6 }}>
                  {advancedFilters.map(filter => (
                    <Button
                      key={filter}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        height: 42,
                        justifyContent: 'space-between',
                        borderRadius: '12px',
                        px: 1.35,
                        bgcolor: 'rgba(255,255,255,0.045)',
                        color: 'rgba(248,250,252,0.82)',
                        border: '1px solid rgba(255,255,255,0.085)',
                        textTransform: 'none',
                        fontWeight: 800
                      }}
                    >
                      {filter}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box sx={{ minWidth: 0, borderLeft: { md: '1px solid rgba(255,255,255,0.07)' }, pl: { md: 2 } }}>
                <Typography variant="overline" sx={{ color: 'rgba(248,250,252,0.48)', fontWeight: 900, letterSpacing: 0 }}>
                  Tags em uso
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.6, mb: 2 }}>
                  {tags.map(tag => (
                    <ConceptChip label={`#${String(tag).replace(/^#/, '')}`} key={tag} />
                  ))}
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 1.5 }} />

                <Typography variant="overline" sx={{ color: 'rgba(248,250,252,0.48)', fontWeight: 900, letterSpacing: 0 }}>
                  Fontes frequentes
                </Typography>
                <Box sx={{ display: 'grid', gap: 0.8, mt: 0.6 }}>
                  {sources.slice(0, 4).map(source => (
                    <Box
                      key={source}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minHeight: 38,
                        px: 1,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        color: 'rgba(248,250,252,0.78)',
                        fontWeight: 780
                      }}
                    >
                      <PublicIcon sx={{ fontSize: 17, color: 'rgba(248,250,252,0.48)' }} />
                      <Typography variant="body2" noWrap translate="no" sx={{ fontWeight: 780 }}>
                        {source}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Button
                  startIcon={<TuneIcon />}
                  fullWidth
                  sx={{
                    mt: 1.5,
                    height: 42,
                    borderRadius: '12px',
                    bgcolor: 'rgba(96,165,250,0.16)',
                    color: '#bfdbfe',
                    textTransform: 'none',
                    fontWeight: 900,
                    '&:hover': { bgcolor: 'rgba(96,165,250,0.22)' }
                  }}
                >
                  Abrir filtros avancados
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const PrototypeArticleCard = ({ article, onOpen }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const imageUrl = getImageUrl(article);
  const originalUrl = article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const tagCount = article.tags?.length || 0;
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const openUrl = (url) => {
    handleMenuClose();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 368,
        borderRadius: '18px',
        overflow: 'hidden',
        bgcolor: '#07111f',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(16,24,40,0.08)'}`,
        cursor: 'pointer',
        transition: 'border-color 140ms ease, background-color 140ms ease',
        position: 'relative',
        '&:hover': {
          borderColor: theme.palette.primary.main
        }
      }}
      onClick={() => onOpen(article)}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={article.title || 'Artigo'}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.src = fallbackImage;
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          filter: 'brightness(0.82)'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(5,10,18,0.26) 0%, rgba(5,10,18,0.06) 42%, rgba(5,10,18,0.72) 100%)'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: 14,
          left: 16,
          right: 52,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: '0.78rem',
          fontWeight: 850,
          letterSpacing: 0
        }}
      >
        <Box sx={{ display: 'grid', placeItems: 'center', '& .MuiSvgIcon-root': { fontSize: 17 } }}>
          <PlatformIcon mediaType={article.mediaType} />
        </Box>
        <Box component="span" translate="no" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {article.mediaType || 'Online'}
        </Box>
      </Box>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 30,
          height: 30,
          color: '#fff',
          bgcolor: 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: 164,
          px: 1.55,
          pt: 1.9,
          pb: 1.25,
          borderRadius: '14px 14px 0 0',
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(145, 164, 184, 0.48)'
            : 'rgba(211, 225, 238, 0.56)',
          color: theme.palette.mode === 'dark' ? '#f6f8fb' : '#17202b',
          boxShadow: '0 -18px 48px rgba(3, 8, 18, 0.18)',
          backdropFilter: 'blur(20px) saturate(1.18)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.18)',
          display: 'flex',
          flexDirection: 'column',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 9,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 56,
            height: 3,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.58)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, minWidth: 0 }}>
          <Typography
            variant="caption"
            translate="no"
            noWrap
            sx={{ color: 'inherit', opacity: 0.72, fontWeight: 800, minWidth: 0 }}
          >
            {source}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.35, flexShrink: 0 }} />
          <Typography variant="caption" noWrap sx={{ color: 'inherit', opacity: 0.72, flexShrink: 0 }}>
            {formatDate(article.date || article.createdAt)}
          </Typography>
        </Box>

        <Typography
          variant="h6"
          translate="no"
          sx={{
            fontSize: '1.02rem',
            fontSize: '0.9rem',
            lineHeight: 1.16,
            fontWeight: 820,
            mb: 1.05,
            minHeight: '2.4em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 'auto' }}>
          <Box
            sx={{
              px: 1.15,
              height: 30,
              borderRadius: 999,
              bgcolor: theme.palette.mode === 'dark' ? '#f8fafc' : '#fff',
              color: '#10151c',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.74rem',
              fontWeight: 900,
              flexShrink: 0
            }}
          >
            {valuation || 'Sem valor'}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, color: 'inherit', opacity: 0.74, fontSize: '0.74rem', fontWeight: 700 }}>
            {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
          </Box>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(16,24,40,0.10)',
              display: 'grid',
              placeItems: 'center',
              color: 'inherit',
              flexShrink: 0
            }}
            title={tagCount ? `${tagCount} tags` : 'Sem tags'}
          >
            <LabelIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={(event) => event.stopPropagation()}>
        <MenuItem onClick={() => openUrl(originalUrl)} disabled={!originalUrl}>
          <OpenInNewIcon fontSize="small" sx={{ mr: 1.25 }} />
          Abrir original
        </MenuItem>
        <MenuItem onClick={() => openUrl(publicUrl)}>
          <PublicIcon fontSize="small" sx={{ mr: 1.25 }} />
          Link publico
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <FavoriteBorderIcon fontSize="small" sx={{ mr: 1.25 }} />
          Favoritar
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <TagIcon fontSize="small" sx={{ mr: 1.25 }} />
          Tags
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <CollectionIcon fontSize="small" sx={{ mr: 1.25 }} />
          Colecao
        </MenuItem>
      </Menu>
    </Box>
  );
};

const EditorialLightArticleCard = ({ article, onOpen }) => {
  const imageUrl = getImageUrl(article);
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const originalUrl = article.siteUrl || article.url;

  const openOriginal = (event) => {
    event.stopPropagation();
    if (originalUrl) window.open(originalUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        height: '100%',
        minHeight: 438,
        borderRadius: '20px',
        overflow: 'visible',
        bgcolor: '#f8fafc',
        color: '#151923',
        cursor: 'pointer',
        boxShadow: '0 10px 16px -14px rgba(15,23,42,0.44), 0 3px 8px -6px rgba(15,23,42,0.24)',
        border: '1px solid rgba(15,23,42,0.08)',
        display: 'flex',
        flexDirection: 'column',
        pt: 8
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 220,
          width: 'calc(100% - 16px)',
          alignSelf: 'center',
          mt: -9.2,
          mb: 0.4,
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: '#10151c',
          boxShadow: '4px 6px 10px -8px rgba(15,23,42,0.50), -4px 6px 10px -8px rgba(15,23,42,0.50), 0 8px 10px -8px rgba(15,23,42,0.38)'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={article.title || 'Artigo'}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = fallbackImage;
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.18), transparent 42%, rgba(3,7,18,0.24))' }} />
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: 1.05,
            height: 30,
            borderRadius: 999,
            bgcolor: 'rgba(15,23,42,0.58)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            fontSize: '0.74rem',
            fontWeight: 850,
            backdropFilter: 'blur(10px)',
            '& .MuiSvgIcon-root': { fontSize: 16 }
          }}
        >
          <PlatformIcon mediaType={article.mediaType} />
          <Box component="span" translate="no" sx={{ maxWidth: 94, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {article.mediaType || 'Online'}
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            px: 1,
            height: 30,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.86)',
            color: '#151923',
            display: 'flex',
            alignItems: 'center',
            gap: 0.45,
            fontSize: '0.74rem',
            fontWeight: 900
          }}
        >
          {valuation || 'Sem valor'}
        </Box>
      </Box>

      <Box sx={{ p: 2.25, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography
          variant="h6"
          translate="no"
          sx={{
            fontSize: '0.96rem',
            lineHeight: 1.18,
            fontWeight: 900,
            mb: 0.7,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Typography variant="body2" translate="no" noWrap sx={{ color: '#6b7280', fontWeight: 750, mb: 1.25 }}>
          {source} - {formatDate(article.date || article.createdAt)}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#4b5563',
            lineHeight: 1.5,
            mb: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.description || article.author || 'Sem resumo disponivel para este artigo.'}
        </Typography>

        <Box sx={{ color: '#9ca3af', fontSize: '0.78rem', fontWeight: 850, mb: 1.6 }}>
          {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              flex: 1,
              height: 38,
              borderRadius: 999,
              border: '1px solid rgba(15,23,42,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.7,
              color: '#151923',
              fontSize: '0.78rem',
              fontWeight: 900
            }}
          >
            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
            Favoritar
          </Box>
          <Box
            onClick={openOriginal}
            sx={{
              flex: 1,
              height: 38,
              borderRadius: 999,
              bgcolor: '#05070b',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.7,
              fontSize: '0.78rem',
              fontWeight: 900
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            Abrir
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EditorialDarkArticleCard = ({ article, onOpen }) => {
  const imageUrl = getImageUrl(article);
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const originalUrl = article.siteUrl || article.url;

  const openOriginal = (event) => {
    event.stopPropagation();
    if (originalUrl) window.open(originalUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        height: '100%',
        minHeight: 438,
        borderRadius: '20px',
        overflow: 'visible',
        bgcolor: '#202124',
        color: '#f8fafc',
        cursor: 'pointer',
        boxShadow: '0 10px 16px -13px rgba(0,0,0,0.72), 0 3px 8px -6px rgba(0,0,0,0.46)',
        display: 'flex',
        flexDirection: 'column',
        pt: 8
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 220,
          width: 'calc(100% - 16px)',
          alignSelf: 'center',
          mt: -9.2,
          mb: 0.4,
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: '#10151c',
          boxShadow: '4px 6px 10px -8px rgba(0,0,0,0.70), -4px 6px 10px -8px rgba(0,0,0,0.70), 0 8px 10px -8px rgba(0,0,0,0.52)'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={article.title || 'Artigo'}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = fallbackImage;
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.20), transparent 42%, rgba(3,7,18,0.28))' }} />
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: 1.05,
            height: 30,
            borderRadius: 999,
            bgcolor: 'rgba(0,0,0,0.54)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            fontSize: '0.74rem',
            fontWeight: 850,
            backdropFilter: 'blur(10px)',
            '& .MuiSvgIcon-root': { fontSize: 16 }
          }}
        >
          <PlatformIcon mediaType={article.mediaType} />
          <Box component="span" translate="no" sx={{ maxWidth: 94, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {article.mediaType || 'Online'}
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            px: 1,
            height: 30,
            borderRadius: 999,
            bgcolor: 'rgba(248,250,252,0.94)',
            color: '#151923',
            display: 'flex',
            alignItems: 'center',
            gap: 0.45,
            fontSize: '0.74rem',
            fontWeight: 900
          }}
        >
          {valuation || 'Sem valor'}
        </Box>
      </Box>

      <Box sx={{ p: 2.25, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography
          variant="h6"
          translate="no"
          sx={{
            fontSize: '0.96rem',
            lineHeight: 1.18,
            fontWeight: 900,
            color: '#9ca3af',
            mb: 0.7,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Typography variant="body2" translate="no" noWrap sx={{ color: 'rgba(248,250,252,0.62)', fontWeight: 750, mb: 1.25 }}>
          {source} - {formatDate(article.date || article.createdAt)}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'rgba(248,250,252,0.74)',
            lineHeight: 1.5,
            mb: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.description || article.author || 'Sem resumo disponivel para este artigo.'}
        </Typography>

        <Box sx={{ color: 'rgba(248,250,252,0.52)', fontSize: '0.78rem', fontWeight: 850, mb: 1.6 }}>
          {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              flex: 1,
              height: 38,
              borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.7,
              color: '#f8fafc',
              fontSize: '0.78rem',
              fontWeight: 900
            }}
          >
            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
            Favoritar
          </Box>
          <Box
            onClick={openOriginal}
            sx={{
              flex: 1,
              height: 38,
              borderRadius: 999,
              bgcolor: '#f8fafc',
              color: '#10151c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.7,
              fontSize: '0.78rem',
              fontWeight: 900
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            Abrir
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EditorialListArticleRow = ({ article, onOpen, variant = 'light' }) => {
  const isDark = variant === 'dark';
  const imageUrl = getImageUrl(article);
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const originalUrl = article.siteUrl || article.url;
  const tags = getArticleTags(article);

  const openOriginal = (event) => {
    event.stopPropagation();
    if (originalUrl) window.open(originalUrl, '_blank', 'noopener,noreferrer');
  };

  const colors = isDark
    ? {
      card: '#202124',
      text: '#f8fafc',
      title: '#f8fafc',
      meta: 'rgba(248,250,252,0.62)',
      body: 'rgba(248,250,252,0.70)',
      muted: 'rgba(248,250,252,0.52)',
      subtle: 'rgba(54,64,74,0.70)',
      chip: 'rgba(54,64,74,0.72)',
      imageShadow: '4px 6px 10px -8px rgba(0,0,0,0.70), -4px 6px 10px -8px rgba(0,0,0,0.70), 0 8px 10px -8px rgba(0,0,0,0.52)',
      cardShadow: '0 10px 16px -13px rgba(0,0,0,0.72), 0 3px 8px -6px rgba(0,0,0,0.46)',
      primaryButtonBg: '#f8fafc',
      primaryButtonText: '#10151c',
      outline: 'rgba(255,255,255,0.12)'
    }
    : {
      card: '#f8fafc',
      text: '#151923',
      title: '#9ca3af',
      meta: '#6b7280',
      body: '#4b5563',
      muted: '#9ca3af',
      subtle: 'rgba(15,23,42,0.08)',
      chip: 'rgba(15,23,42,0.08)',
      imageShadow: '4px 6px 10px -8px rgba(15,23,42,0.50), -4px 6px 10px -8px rgba(15,23,42,0.50), 0 8px 10px -8px rgba(15,23,42,0.38)',
      cardShadow: '0 10px 16px -14px rgba(15,23,42,0.44), 0 3px 8px -6px rgba(15,23,42,0.24)',
      primaryButtonBg: '#05070b',
      primaryButtonText: '#fff',
      outline: 'rgba(15,23,42,0.14)'
    };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '156px minmax(0, 1fr)' },
        gap: { xs: 0, sm: 2.1 },
        alignItems: 'stretch',
        minHeight: { xs: 330, sm: 172 },
        borderRadius: '20px',
        bgcolor: colors.card,
        color: colors.text,
        cursor: 'pointer',
        overflow: 'visible',
        boxShadow: colors.cardShadow,
        border: isDark ? 0 : '1px solid rgba(15,23,42,0.08)',
        p: { xs: 1, sm: 1.2 },
        pl: { xs: 1, sm: 1.45 }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: { xs: 180, sm: 'calc(100% + 18px)' },
          minHeight: { sm: 166 },
          mt: { xs: -3.6, sm: -2.1 },
          ml: { xs: 0, sm: -2.2 },
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: '#10151c',
          boxShadow: colors.imageShadow
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={article.title || 'Artigo'}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = fallbackImage;
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.14), transparent 45%, rgba(3,7,18,0.20))' }} />
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            px: 0.9,
            height: 28,
            borderRadius: 999,
            bgcolor: isDark ? 'rgba(0,0,0,0.54)' : 'rgba(15,23,42,0.58)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 0.55,
            fontSize: '0.7rem',
            fontWeight: 850,
            backdropFilter: 'blur(10px)',
            '& .MuiSvgIcon-root': { fontSize: 15 }
          }}
        >
          <PlatformIcon mediaType={article.mediaType} />
          <Box component="span" translate="no" sx={{ maxWidth: 86, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {article.mediaType || 'Online'}
          </Box>
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, p: { xs: 1.35, sm: 1.15 }, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.7, minWidth: 0 }}>
          <Typography variant="caption" translate="no" noWrap sx={{ color: colors.meta, fontWeight: 850, minWidth: 0 }}>
            {source}
          </Typography>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: colors.meta, opacity: 0.45, flexShrink: 0 }} />
          <Typography variant="caption" noWrap sx={{ color: colors.meta, fontWeight: 800, flexShrink: 0 }}>
            {formatDate(article.date || article.createdAt)}
          </Typography>
        </Box>

        <Typography
          variant="h6"
          translate="no"
          sx={{
            color: colors.title,
            fontSize: { xs: '1rem', sm: '1.03rem' },
            lineHeight: 1.16,
            fontWeight: 900,
            mb: 0.75,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: colors.body,
            lineHeight: 1.45,
            fontSize: '0.82rem',
            mb: 1.15,
            display: '-webkit-box',
            WebkitLineClamp: { xs: 3, sm: 2 },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.description || article.author || 'Sem resumo disponivel para este artigo.'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 'auto' }}>
          <Box sx={{ px: 1.15, height: 32, borderRadius: 999, bgcolor: isDark ? '#f8fafc' : '#fff', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 900 }}>
            {valuation || 'Sem valor'}
          </Box>
          <Box sx={{ px: 1.1, height: 32, borderRadius: 999, bgcolor: colors.chip, color: isDark ? '#f8fafc' : colors.muted, display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 850 }}>
            {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
          </Box>
          {tags.slice(0, 1).map(tag => (
            <Box key={`list-tag-${article._id}-${tag}`} translate="no" sx={{ px: 1, height: 28, borderRadius: 999, bgcolor: colors.subtle, color: colors.meta, display: 'flex', alignItems: 'center', fontSize: '0.72rem', fontWeight: 850, maxWidth: 130 }}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {String(tag).replace(/^#/, '')}
              </Box>
            </Box>
          ))}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>
            <IconButton
              size="small"
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: 32,
                height: 32,
                border: `1px solid ${colors.outline}`,
                color: colors.text,
                '&:hover': { bgcolor: colors.subtle }
              }}
            >
              <FavoriteBorderIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={openOriginal}
              sx={{
                width: 32,
                height: 32,
                bgcolor: colors.primaryButtonBg,
                color: colors.primaryButtonText,
                '&:hover': { bgcolor: colors.primaryButtonBg }
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EditorialDarkArticleModal = ({ article, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [fullArticle, setFullArticle] = React.useState(null);
  const [contentLoading, setContentLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const fetchFullArticle = async () => {
      if (!open || !article?._id) {
        setFullArticle(null);
        return;
      }

      setFullArticle(article);
      setContentLoading(true);
      try {
        const response = await api.get(`/articles/${article._id}`);
        if (!cancelled) {
          setFullArticle(response.data?.data || article);
        }
      } catch (error) {
        if (!cancelled) {
          setFullArticle(article);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    };

    fetchFullArticle();

    return () => {
      cancelled = true;
    };
  }, [article, open]);

  if (!article) return null;

  const modalArticle = fullArticle || article;
  const imageUrl = getImageUrl(modalArticle);
  const source = modalArticle.siteName || modalArticle.author || modalArticle.mediaType || 'Fonte desconhecida';
  const originalUrl = modalArticle.siteUrl || modalArticle.url || article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const valuation = formatCurrency(modalArticle.valuation);
  const reach = modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber || modalArticle.domainInfo?.estimatedAudience;
  const monthlyAudience = modalArticle.monthlyAudience || modalArticle.audienceMonthly || modalArticle.domainInfo?.monthlyVisits || modalArticle.domainInfo?.estimatedAudience;
  const dailyAudience = modalArticle.dailyAudience || modalArticle.audienceDaily || (monthlyAudience ? Math.round(Number(monthlyAudience) / 30) : null);
  const pageViews = modalArticle.pageViews || modalArticle.viewsNumber || modalArticle.views;
  const dailyPageViews = modalArticle.dailyPageViews || modalArticle.pageViewsDaily;
  const tagCount = Array.isArray(modalArticle.tags) ? modalArticle.tags.length : 0;
  const heroTags = getArticleTags(modalArticle);
  const collectionCount = getArticleCollections(modalArticle).length;
  const occupancy = modalArticle.editorialOccupancy || {};
  const sentiment = modalArticle.sentimentAnalysis?.sentiment || modalArticle.sentiment || modalArticle.sentimentLabel;
  const youtubeId = getModalYouTubeId(modalArticle);
  const videoUrl = getPlayableVideoUrl(modalArticle);
  const isVideoArticle = Boolean(youtubeId || videoUrl);
  const isInstagramArticle = modalArticle.mediaType === 'Instagram';
  const contentText = cleanArticleText(
    modalArticle.content || modalArticle.searchableContent || modalArticle.description || article.description
  );
  const metricCards = [
    { label: 'Valoracao', value: valuation || 'Sem valor' },
    { label: 'Alcance', value: reach ? formatCompact(reach) : 'Sem alcance' },
    { label: 'Audiencia mensal', value: monthlyAudience ? formatCompact(monthlyAudience) : '-' },
    { label: 'Page views', value: pageViews ? formatCompact(pageViews) : '-' },
    { label: 'Tier', value: modalArticle.tier || modalArticle.sourceTier || '-' },
    { label: 'Sentimento', value: sentiment || '-' }
  ];
  const infoRows = [
    ['Tipo', modalArticle.mediaType || '-'],
    ['Fonte', source],
    ['Data', formatDate(modalArticle.date || modalArticle.createdAt)],
    ['Aud. diaria', dailyAudience ? formatCompact(dailyAudience) : '-'],
    ['PV diario', dailyPageViews ? formatCompact(dailyPageViews) : '-'],
    ['Tags', tagCount || '-'],
    ['Colecoes', collectionCount || '-'],
    ['Ocupacao Editorial', occupancy.valueCm ? `${occupancy.valueCm} cm` : (occupancy.valueCm2 ? `${occupancy.valueCm2} cm2` : '-')],
    ['Participacao', occupancy.brandParticipationPercent ? `${occupancy.brandParticipationPercent}%` : '-'],
    ['Exposicao', modalArticle.exposure || occupancy.exposure || '-']
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 'min(1120px, calc(100vw - 36px))',
          height: fullScreen ? '100%' : 'min(88vh, 780px)',
          borderRadius: fullScreen ? 0 : '26px',
          overflow: 'hidden',
          bgcolor: '#202124',
          color: '#f8fafc',
          boxShadow: '0 18px 38px -20px rgba(0,0,0,0.76), 0 10px 22px -18px rgba(0,0,0,0.56)'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.62fr) 330px' },
          bgcolor: '#202124',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
          <Box sx={{ px: { xs: 2.2, md: 4 }, pt: { xs: 7.5, md: 5 }, pb: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.05, color: 'rgba(248,250,252,0.62)', minWidth: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, flexShrink: 0 }}>
                <PlatformIcon mediaType={modalArticle.mediaType} />
                <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850 }}>
                  {modalArticle.mediaType || 'Online'}
                </Typography>
              </Box>
              {heroTags.length > 0 && (
                <Box sx={{ ml: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.6, flexWrap: 'wrap', minWidth: 0 }}>
                  {heroTags.slice(0, 4).map(tag => (
                    <Box
                      key={`header-tag-${tag}`}
                      translate="no"
                      sx={{
                        maxWidth: 132,
                        px: 1,
                        height: 26,
                        borderRadius: 999,
                        bgcolor: 'rgba(54,64,74,0.62)',
                        color: 'rgba(248,250,252,0.78)',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 850
                      }}
                    >
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {String(tag).replace(/^#/, '')}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Typography
              variant="h3"
              translate="no"
              sx={{
                maxWidth: 760,
                fontSize: { xs: '1.18rem', md: '1.42rem' },
                lineHeight: 1.08,
                fontWeight: 900,
                color: '#f8fafc',
                mb: 1.2
              }}
            >
              {modalArticle.title || 'Sem titulo'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 4.4, color: 'rgba(248,250,252,0.62)', minWidth: 0 }}>
              <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850, minWidth: 0, maxWidth: { xs: '58%', sm: '70%' } }}>
                {source}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.42, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 800, flexShrink: 0 }}>
                {formatDate(modalArticle.date || modalArticle.createdAt)}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                width: isInstagramArticle ? { xs: 'min(100%, 430px)', md: 'min(100%, 470px)' } : (isVideoArticle ? '100%' : { xs: 'calc(100% + 4px)', md: 'calc(100% + 18px)' }),
                ml: isInstagramArticle ? 'auto' : (isVideoArticle ? 0 : { xs: -0.25, md: -1.1 }),
                mr: isInstagramArticle ? 'auto' : 0,
                mb: isVideoArticle || isInstagramArticle ? 2.4 : -4.6,
                aspectRatio: isInstagramArticle ? '4 / 5' : '16 / 9',
                borderRadius: '20px',
                overflow: 'hidden',
                bgcolor: '#10151c',
                boxShadow: '7px 10px 18px -10px rgba(0,0,0,0.82), -7px 10px 18px -10px rgba(0,0,0,0.82), 0 16px 22px -12px rgba(0,0,0,0.62)'
              }}
            >
              {youtubeId ? (
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={modalArticle.title || 'Video do YouTube'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : videoUrl ? (
                <Box component="video" src={videoUrl} controls playsInline poster={imageUrl} sx={{ width: '100%', height: '100%', display: 'block', objectFit: isInstagramArticle ? 'contain' : 'cover', bgcolor: '#000' }} />
              ) : (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={modalArticle.title || 'Artigo'}
                  onError={(event) => {
                    event.currentTarget.src = fallbackImage;
                  }}
                  sx={{ width: '100%', height: '100%', display: 'block', objectFit: isInstagramArticle ? 'contain' : 'cover', bgcolor: isInstagramArticle ? '#000' : 'transparent' }}
                />
              )}
              {!isVideoArticle && (
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.10), transparent 52%, rgba(3,7,18,0.32))' }} />
              )}
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                borderRadius: '20px',
                bgcolor: '#202124',
                px: { xs: 2.2, md: 3 },
                pt: isVideoArticle ? { xs: 2.2, md: 2.6 } : { xs: 7.2, md: 8 },
                pb: { xs: 2.5, md: 3 },
                minHeight: 280,
                boxShadow: '0 12px 24px -18px rgba(0,0,0,0.62)'
              }}
            >
              <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.62)', lineHeight: 1.55, mb: 2 }}>
                {modalArticle.description || modalArticle.author || 'Sem resumo disponivel para este artigo.'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(248,250,252,0.74)',
                  lineHeight: 1.74,
                  whiteSpace: 'pre-line',
                  fontSize: { xs: '0.9rem', md: '0.95rem' }
                }}
              >
                {contentLoading ? 'Carregando conteudo...' : (contentText || 'Sem conteudo disponivel para este artigo.')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 2.2, md: 2.2 },
            py: { xs: 2.5, md: 6.8 },
            bgcolor: '#202124',
            borderLeft: { xs: 0, md: '1px solid rgba(248,250,252,0.035)' },
            scrollbarWidth: 'thin'
          }}
        >
          <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 900, mb: 0.4 }}>
            Informacoes
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.52)', mb: 2.2 }}>
            Dados individuais desta publicacao
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 2.2 }}>
            {metricCards.map(metric => (
              <Box key={metric.label} sx={{ borderRadius: '14px', bgcolor: '#202124', p: 1.25, minHeight: 72 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'rgba(248,250,252,0.52)', fontWeight: 850, mb: 0.5 }}>
                  {metric.label}
                </Typography>
                <Typography variant="body2" translate="no" noWrap sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ borderRadius: '16px', bgcolor: '#202124', overflow: 'hidden', mb: 2.4 }}>
            {infoRows.map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(92px, 0.8fr) minmax(0, 1fr)',
                  gap: 1,
                  px: 1.35,
                  py: 1,
                  borderTop: '1px solid rgba(248,250,252,0.055)',
                  '&:first-of-type': { borderTop: 0 }
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.52)', fontWeight: 800 }}>
                  {label}
                </Typography>
                <Typography variant="caption" translate="no" noWrap sx={{ color: '#f8fafc', fontWeight: 900, textAlign: 'right', minWidth: 0 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, position: { md: 'sticky' }, bottom: 0, py: 1.2, bgcolor: '#202124' }}>
            <IconButton
              onClick={() => originalUrl && window.open(originalUrl, '_blank', 'noopener,noreferrer')}
              disabled={!originalUrl}
              sx={{ width: 42, height: 42, bgcolor: '#fff', color: '#10151c', '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              sx={{ width: 42, height: 42, bgcolor: 'rgba(54,64,74,0.72)', color: '#fff', '&:hover': { bgcolor: 'rgba(54,64,74,0.90)' } }}
            >
              <PublicIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            zIndex: 5,
            top: { xs: 12, md: 18 },
            right: { xs: 12, md: 18 },
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.28)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.42)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
};

const EditorialLightArticleModal = ({ article, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [fullArticle, setFullArticle] = React.useState(null);
  const [contentLoading, setContentLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const fetchFullArticle = async () => {
      if (!open || !article?._id) {
        setFullArticle(null);
        return;
      }

      setFullArticle(article);
      setContentLoading(true);
      try {
        const response = await api.get(`/articles/${article._id}`);
        if (!cancelled) {
          setFullArticle(response.data?.data || article);
        }
      } catch (error) {
        if (!cancelled) {
          setFullArticle(article);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    };

    fetchFullArticle();

    return () => {
      cancelled = true;
    };
  }, [article, open]);

  if (!article) return null;

  const modalArticle = fullArticle || article;
  const imageUrl = getImageUrl(modalArticle);
  const source = modalArticle.siteName || modalArticle.author || modalArticle.mediaType || 'Fonte desconhecida';
  const originalUrl = modalArticle.siteUrl || modalArticle.url || article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const valuation = formatCurrency(modalArticle.valuation);
  const reach = modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber || modalArticle.domainInfo?.estimatedAudience;
  const monthlyAudience = modalArticle.monthlyAudience || modalArticle.audienceMonthly || modalArticle.domainInfo?.monthlyVisits || modalArticle.domainInfo?.estimatedAudience;
  const dailyAudience = modalArticle.dailyAudience || modalArticle.audienceDaily || (monthlyAudience ? Math.round(Number(monthlyAudience) / 30) : null);
  const pageViews = modalArticle.pageViews || modalArticle.viewsNumber || modalArticle.views;
  const dailyPageViews = modalArticle.dailyPageViews || modalArticle.pageViewsDaily;
  const tagCount = Array.isArray(modalArticle.tags) ? modalArticle.tags.length : 0;
  const heroTags = getArticleTags(modalArticle);
  const collectionCount = getArticleCollections(modalArticle).length;
  const occupancy = modalArticle.editorialOccupancy || {};
  const sentiment = modalArticle.sentimentAnalysis?.sentiment || modalArticle.sentiment || modalArticle.sentimentLabel;
  const youtubeId = getModalYouTubeId(modalArticle);
  const videoUrl = getPlayableVideoUrl(modalArticle);
  const isVideoArticle = Boolean(youtubeId || videoUrl);
  const isInstagramArticle = modalArticle.mediaType === 'Instagram';
  const contentText = cleanArticleText(
    modalArticle.content || modalArticle.searchableContent || modalArticle.description || article.description
  );
  const metricCards = [
    { label: 'Valoracao', value: valuation || 'Sem valor' },
    { label: 'Alcance', value: reach ? formatCompact(reach) : 'Sem alcance' },
    { label: 'Audiencia mensal', value: monthlyAudience ? formatCompact(monthlyAudience) : '-' },
    { label: 'Page views', value: pageViews ? formatCompact(pageViews) : '-' },
    { label: 'Tier', value: modalArticle.tier || modalArticle.sourceTier || '-' },
    { label: 'Sentimento', value: sentiment || '-' }
  ];
  const infoRows = [
    ['Tipo', modalArticle.mediaType || '-'],
    ['Fonte', source],
    ['Data', formatDate(modalArticle.date || modalArticle.createdAt)],
    ['Aud. diaria', dailyAudience ? formatCompact(dailyAudience) : '-'],
    ['PV diario', dailyPageViews ? formatCompact(dailyPageViews) : '-'],
    ['Tags', tagCount || '-'],
    ['Colecoes', collectionCount || '-'],
    ['Ocupacao Editorial', occupancy.valueCm ? `${occupancy.valueCm} cm` : (occupancy.valueCm2 ? `${occupancy.valueCm2} cm2` : '-')],
    ['Participacao', occupancy.brandParticipationPercent ? `${occupancy.brandParticipationPercent}%` : '-'],
    ['Exposicao', modalArticle.exposure || occupancy.exposure || '-']
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 'min(1120px, calc(100vw - 36px))',
          height: fullScreen ? '100%' : 'min(88vh, 780px)',
          borderRadius: fullScreen ? 0 : '26px',
          overflow: 'hidden',
          bgcolor: '#f8fafc',
          color: '#151923',
          boxShadow: '0 20px 46px -22px rgba(15,23,42,0.42), 0 12px 28px -22px rgba(15,23,42,0.30)'
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.62fr) 330px' },
          bgcolor: '#f8fafc',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
          <Box sx={{ px: { xs: 2.2, md: 4 }, pt: { xs: 7.5, md: 5 }, pb: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.05, color: '#6b7280', minWidth: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, flexShrink: 0 }}>
                <PlatformIcon mediaType={modalArticle.mediaType} />
                <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850 }}>
                  {modalArticle.mediaType || 'Online'}
                </Typography>
              </Box>
              {heroTags.length > 0 && (
                <Box sx={{ ml: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 0.6, flexWrap: 'wrap', minWidth: 0 }}>
                  {heroTags.slice(0, 4).map(tag => (
                    <Box
                      key={`light-header-tag-${tag}`}
                      translate="no"
                      sx={{
                        maxWidth: 132,
                        px: 1,
                        height: 26,
                        borderRadius: 999,
                        bgcolor: 'rgba(15,23,42,0.08)',
                        color: '#4b5563',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 850
                      }}
                    >
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {String(tag).replace(/^#/, '')}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Typography
              variant="h3"
              translate="no"
              sx={{
                maxWidth: 760,
                fontSize: { xs: '1.18rem', md: '1.42rem' },
                lineHeight: 1.08,
                fontWeight: 900,
                color: '#9ca3af',
                mb: 1.2
              }}
            >
              {modalArticle.title || 'Sem titulo'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 4.4, color: '#6b7280', minWidth: 0 }}>
              <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850, minWidth: 0, maxWidth: { xs: '58%', sm: '70%' } }}>
                {source}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.42, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 800, flexShrink: 0 }}>
                {formatDate(modalArticle.date || modalArticle.createdAt)}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                width: isInstagramArticle ? { xs: 'min(100%, 430px)', md: 'min(100%, 470px)' } : (isVideoArticle ? '100%' : { xs: 'calc(100% + 4px)', md: 'calc(100% + 18px)' }),
                ml: isInstagramArticle ? 'auto' : (isVideoArticle ? 0 : { xs: -0.25, md: -1.1 }),
                mr: isInstagramArticle ? 'auto' : 0,
                mb: isVideoArticle || isInstagramArticle ? 2.4 : -4.6,
                aspectRatio: isInstagramArticle ? '4 / 5' : '16 / 9',
                borderRadius: '20px',
                overflow: 'hidden',
                bgcolor: '#10151c',
                boxShadow: '7px 10px 18px -10px rgba(15,23,42,0.58), -7px 10px 18px -10px rgba(15,23,42,0.58), 0 16px 22px -12px rgba(15,23,42,0.46)'
              }}
            >
              {youtubeId ? (
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={modalArticle.title || 'Video do YouTube'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : videoUrl ? (
                <Box component="video" src={videoUrl} controls playsInline poster={imageUrl} sx={{ width: '100%', height: '100%', display: 'block', objectFit: isInstagramArticle ? 'contain' : 'cover', bgcolor: '#000' }} />
              ) : (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={modalArticle.title || 'Artigo'}
                  onError={(event) => {
                    event.currentTarget.src = fallbackImage;
                  }}
                  sx={{ width: '100%', height: '100%', display: 'block', objectFit: isInstagramArticle ? 'contain' : 'cover', bgcolor: isInstagramArticle ? '#000' : 'transparent' }}
                />
              )}
              {!isVideoArticle && (
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.08), transparent 52%, rgba(3,7,18,0.18))' }} />
              )}
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                borderRadius: '20px',
                bgcolor: '#f8fafc',
                px: { xs: 2.2, md: 3 },
                pt: isVideoArticle ? { xs: 2.2, md: 2.6 } : { xs: 7.2, md: 8 },
                pb: { xs: 2.5, md: 3 },
                minHeight: 280,
                boxShadow: '0 12px 24px -18px rgba(15,23,42,0.22)'
              }}
            >
              <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.55, mb: 2 }}>
                {modalArticle.description || modalArticle.author || 'Sem resumo disponivel para este artigo.'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#374151',
                  lineHeight: 1.74,
                  whiteSpace: 'pre-line',
                  fontSize: { xs: '0.9rem', md: '0.95rem' }
                }}
              >
                {contentLoading ? 'Carregando conteudo...' : (contentText || 'Sem conteudo disponivel para este artigo.')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 2.2, md: 2.2 },
            py: { xs: 2.5, md: 6.8 },
            bgcolor: '#f8fafc',
            borderLeft: { xs: 0, md: '1px solid rgba(15,23,42,0.06)' },
            scrollbarWidth: 'thin'
          }}
        >
          <Typography variant="h6" sx={{ color: '#6b7280', fontWeight: 900, mb: 0.4 }}>
            Informacoes
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2.2 }}>
            Dados individuais desta publicacao
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 2.2 }}>
            {metricCards.map(metric => (
              <Box key={metric.label} sx={{ borderRadius: '14px', bgcolor: '#f8fafc', p: 1.25, minHeight: 72, boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.08)' }}>
                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', fontWeight: 850, mb: 0.5 }}>
                  {metric.label}
                </Typography>
                <Typography variant="body2" translate="no" noWrap sx={{ color: '#9ca3af', fontWeight: 900 }}>
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ borderRadius: '16px', bgcolor: '#f8fafc', overflow: 'hidden', mb: 2.4, boxShadow: 'inset 0 0 0 1px rgba(15,23,42,0.08)' }}>
            {infoRows.map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(92px, 0.8fr) minmax(0, 1fr)',
                  gap: 1,
                  px: 1.35,
                  py: 1,
                  borderTop: '1px solid rgba(15,23,42,0.07)',
                  '&:first-of-type': { borderTop: 0 }
                }}
              >
                <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 800 }}>
                  {label}
                </Typography>
                <Typography variant="caption" translate="no" noWrap sx={{ color: '#9ca3af', fontWeight: 900, textAlign: 'right', minWidth: 0 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, position: { md: 'sticky' }, bottom: 0, py: 1.2, bgcolor: '#f8fafc' }}>
            <IconButton
              onClick={() => originalUrl && window.open(originalUrl, '_blank', 'noopener,noreferrer')}
              disabled={!originalUrl}
              sx={{ width: 42, height: 42, bgcolor: '#05070b', color: '#fff', '&:hover': { bgcolor: '#111827' } }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              sx={{ width: 42, height: 42, bgcolor: 'rgba(15,23,42,0.08)', color: '#151923', '&:hover': { bgcolor: 'rgba(15,23,42,0.12)' } }}
            >
              <PublicIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            zIndex: 5,
            top: { xs: 12, md: 18 },
            right: { xs: 12, md: 18 },
            color: '#151923',
            bgcolor: 'rgba(255,255,255,0.80)',
            '&:hover': { bgcolor: '#fff' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
};

const HybridArticleCard = ({ article, onOpen }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const imageUrl = getImageUrl(article);
  const originalUrl = article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const tagLabel = article.tags?.[0]?.name || article.tags?.[0] || article.keywords?.[0] || article.mediaType || 'tag';

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const openUrl = (url) => {
    handleMenuClose();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        height: '100%',
        minHeight: 386,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#07111f',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'border-color 140ms ease',
        '&:hover': { borderColor: 'rgba(255,255,255,0.18)' }
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={article.title || 'Artigo'}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.src = fallbackImage;
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          filter: 'brightness(0.82)'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(3,7,18,0.12) 0%, rgba(3,7,18,0.04) 34%, rgba(3,7,18,0.56) 100%)'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          width: 30,
          height: 30,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.50)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          '& .MuiSvgIcon-root': { fontSize: 18 }
        }}
      >
        <PlatformIcon mediaType={article.mediaType} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 46,
          width: 30,
          height: 30,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.50)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <PublicIcon sx={{ fontSize: 17 }} />
      </Box>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 30,
          height: 30,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.50)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.68)' }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          px: 2,
          pt: 2.1,
          pb: 1.7,
          color: '#f8fafc',
          minHeight: 214,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px 14px 0 0',
          bgcolor: 'rgba(88, 101, 116, 0.50)',
          boxShadow: '0 -18px 48px rgba(3, 8, 18, 0.22)',
          backdropFilter: 'blur(20px) saturate(1.15)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 9,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 56,
            height: 3,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.52)'
          }
        }}
      >
        <Typography
          variant="subtitle1"
          translate="no"
          sx={{
            fontSize: '0.96rem',
            lineHeight: 1.2,
            fontWeight: 850,
            mb: 1,
            minHeight: '2.4em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: 0, mb: 1.25 }}>
          <Box sx={{ color: '#38bdf8', display: 'grid', placeItems: 'center', '& .MuiSvgIcon-root': { fontSize: 16 } }}>
            <PlatformIcon mediaType={article.mediaType} />
          </Box>
          <Typography variant="body2" translate="no" noWrap sx={{ minWidth: 0, flex: 1, color: 'rgba(248,250,252,0.70)', fontWeight: 650 }}>
            {source}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.38)', flexShrink: 0 }}>
            {formatDate(article.date || article.createdAt).replace(' de ', '/').replace(' de ', '/')}
          </Typography>
        </Box>

        <Box
          sx={{
            alignSelf: 'flex-start',
            px: 1.15,
            height: 28,
            maxWidth: '100%',
            borderRadius: '7px',
            bgcolor: 'rgba(255,255,255,0.08)',
            color: 'rgba(248,250,252,0.58)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.65,
            fontSize: '0.76rem',
            fontWeight: 800,
            mb: 'auto'
          }}
        >
          <TagIcon sx={{ fontSize: 14 }} />
          <Box component="span" translate="no" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {String(tagLabel).replace(/^#/, '')}
          </Box>
        </Box>

        <Box
          sx={{
            mx: -2,
            mt: 1.7,
            px: 2,
            pt: 1.35,
            borderTop: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.15,
            color: 'rgba(248,250,252,0.64)',
            fontSize: '0.84rem',
            fontWeight: 850
          }}
        >
          <Box component="span" sx={{ opacity: 0.72 }}>$</Box>
          <Box component="span">{valuation || 'Sem valor'}</Box>
          <Box component="span" sx={{ opacity: 0.52 }}>•</Box>
          <Box component="span">{reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}</Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={(event) => event.stopPropagation()}>
        <MenuItem onClick={() => openUrl(originalUrl)} disabled={!originalUrl}>
          <OpenInNewIcon fontSize="small" sx={{ mr: 1.25 }} />
          Abrir original
        </MenuItem>
        <MenuItem onClick={() => openUrl(publicUrl)}>
          <PublicIcon fontSize="small" sx={{ mr: 1.25 }} />
          Link publico
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <TagIcon fontSize="small" sx={{ mr: 1.25 }} />
          Tags
        </MenuItem>
      </Menu>
    </Box>
  );
};

const StackedHybridArticleCard = ({ article, onOpen }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const imageUrl = getImageUrl(article);
  const originalUrl = article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const valuation = formatCurrency(article.valuation);
  const reach = article.viewsNumber || article.audience || article.likesNumber || article.domainInfo?.estimatedAudience;
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const tagLabel = article.tags?.[0]?.name || article.tags?.[0] || article.keywords?.[0] || article.mediaType || 'tag';

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const openUrl = (url) => {
    handleMenuClose();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        position: 'relative',
        minHeight: { xs: 260, md: 226 },
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: '#07111f',
        border: '1px solid rgba(255,255,255,0.07)',
        cursor: 'pointer'
      }}
    >
      <Box
        component="img"
        src={imageUrl}
        alt={article.title || 'Artigo'}
        loading="lazy"
        onError={(event) => {
          event.currentTarget.src = fallbackImage;
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: 'brightness(0.78)'
        }}
      />
      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(3,7,18,0.20), rgba(3,7,18,0.04) 36%, rgba(3,7,18,0.60) 100%)' }} />

      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          color: '#fff',
          fontWeight: 850,
          '& .MuiSvgIcon-root': { fontSize: 18 }
        }}
      >
        <PlatformIcon mediaType={article.mediaType} />
        <Typography variant="body2" translate="no" sx={{ fontWeight: 850 }}>
          {article.mediaType || 'Online'}
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 32,
          height: 32,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.42)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.62)' }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          top: { xs: 58, md: 24 },
          right: { xs: 12, md: 14 },
          bottom: { xs: 12, md: 14 },
          width: { xs: 'calc(100% - 24px)', md: 'min(55%, 560px)' },
          px: { xs: 2, md: 2.5 },
          pt: { xs: 2.1, md: 2.5 },
          pb: { xs: 1.7, md: 2 },
          borderRadius: '14px 14px 12px 12px',
          bgcolor: 'rgba(88, 101, 116, 0.48)',
          color: '#f8fafc',
          boxShadow: '0 18px 48px rgba(3, 8, 18, 0.25)',
          backdropFilter: 'blur(20px) saturate(1.15)',
          WebkitBackdropFilter: 'blur(20px) saturate(1.15)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography
          variant="h6"
          translate="no"
          sx={{
            fontSize: { xs: '1.02rem', md: '1.26rem' },
            lineHeight: 1.12,
            fontWeight: 900,
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {article.title || 'Sem titulo'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: 0, mb: 1.2 }}>
          <Typography variant="body2" translate="no" noWrap sx={{ minWidth: 0, flex: 1, color: 'rgba(248,250,252,0.76)', fontWeight: 750 }}>
            {source}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.52)', flexShrink: 0 }}>
            {formatDate(article.date || article.createdAt)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 'auto' }}>
          <Box sx={{ px: 1.15, height: 30, borderRadius: 999, bgcolor: '#f8fafc', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 900 }}>
            {valuation || 'Sem valor'}
          </Box>
          <Box sx={{ px: 1.1, height: 30, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 800 }}>
            {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
          </Box>
          <Box sx={{ px: 1.1, height: 30, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', gap: 0.65, fontSize: '0.78rem', fontWeight: 800, minWidth: 0 }}>
            <TagIcon sx={{ fontSize: 15 }} />
            <Box component="span" translate="no" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {String(tagLabel).replace(/^#/, '')}
            </Box>
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={(event) => event.stopPropagation()}>
        <MenuItem onClick={() => openUrl(originalUrl)} disabled={!originalUrl}>
          <OpenInNewIcon fontSize="small" sx={{ mr: 1.25 }} />
          Abrir original
        </MenuItem>
        <MenuItem onClick={() => openUrl(publicUrl)}>
          <PublicIcon fontSize="small" sx={{ mr: 1.25 }} />
          Link publico
        </MenuItem>
      </Menu>
    </Box>
  );
};

const SimilarClusterCard = ({ articles, onOpen, onOpenCluster }) => {
  const [expanded, setExpanded] = React.useState(false);
  const mainArticle = articles[0];
  const similarArticles = articles.slice(1, 6);
  const stackOffset = 4;
  const stackHeight = 416;

  if (!mainArticle) return null;

  const imageUrl = getImageUrl(mainArticle);
  const totalValuation = articles.reduce((sum, item) => sum + Number(item.valuation || 0), 0);
  const totalReach = articles.reduce((sum, item) => {
    const reach = item.viewsNumber || item.audience || item.likesNumber || item.domainInfo?.estimatedAudience || 0;
    return sum + Number(reach || 0);
  }, 0);
  const source = mainArticle.siteName || mainArticle.author || mainArticle.mediaType || 'Fonte desconhecida';

  return (
    <Box sx={{ position: 'relative', pb: expanded ? 0 : 4.8 }}>
      {!expanded && similarArticles.slice(0, 2).map((item, index) => (
        <Box
          key={`cluster-shadow-${item._id}`}
          sx={{
            position: 'absolute',
            top: `${stackOffset * (index + 1)}px`,
            left: `${stackOffset * (index + 1)}px`,
            width: '100%',
            height: stackHeight,
            borderRadius: '14px',
            bgcolor: index === 0 ? '#424b55' : '#363f49',
            boxShadow: index === 0 ? '0 16px 30px rgba(0,0,0,0.14)' : '0 18px 34px rgba(0,0,0,0.12)',
            zIndex: 2 - index,
            pointerEvents: 'none'
          }}
        />
      ))}

      <Box
        onClick={() => (onOpenCluster ? onOpenCluster(articles) : onOpen(mainArticle))}
        sx={{
          position: 'relative',
          zIndex: 3,
          height: stackHeight,
          borderRadius: '14px',
          overflow: 'hidden',
          bgcolor: '#07111f',
          border: 0,
          cursor: 'pointer',
          boxShadow: '0 18px 48px rgba(0,0,0,0.28)'
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={mainArticle.title || 'Artigo'}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = fallbackImage;
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.80)'
          }}
        />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(3,7,18,0.16), rgba(3,7,18,0.04) 34%, rgba(3,7,18,0.66) 100%)' }} />

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 14,
            right: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            color: '#fff',
            '& .MuiSvgIcon-root': { fontSize: 18 }
          }}
        >
          <PlatformIcon mediaType={mainArticle.mediaType} />
          <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850 }}>
            {mainArticle.mediaType || 'Online'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box
            onClick={(event) => {
              event.stopPropagation();
              setExpanded(prev => !prev);
            }}
            sx={{
              px: 1.15,
              height: 28,
              borderRadius: 999,
              bgcolor: 'rgba(0,0,0,0.48)',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.74rem',
              fontWeight: 850
            }}
          >
            +{similarArticles.length} similares
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -2,
            px: 2,
            pt: 2.05,
            pb: 1.7,
            minHeight: 216,
            borderRadius: '16px 16px 14px 14px',
            bgcolor: 'rgba(88, 101, 116, 0.50)',
            color: '#f8fafc',
            boxShadow: '0 -18px 48px rgba(3, 8, 18, 0.24)',
            backdropFilter: 'blur(20px) saturate(1.15)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.15)',
            display: 'flex',
            flexDirection: 'column',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 9,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 56,
              height: 3,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.52)'
            }
          }}
        >
          <Typography
            variant="subtitle1"
            translate="no"
            sx={{
              fontSize: '0.98rem',
              lineHeight: 1.18,
              fontWeight: 900,
              mb: 0.9,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {mainArticle.title || 'Sem titulo'}
          </Typography>

          <Box sx={{ minWidth: 0, mb: 'auto' }}>
            <Typography variant="body2" translate="no" noWrap sx={{ color: 'rgba(248,250,252,0.74)', fontWeight: 750 }}>
              {source}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(248,250,252,0.48)', mt: 0.25 }}>
              {formatDate(mainArticle.date || mainArticle.createdAt)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ px: 1.15, height: 30, borderRadius: 999, bgcolor: '#f8fafc', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 900 }}>
              {formatCurrency(totalValuation) || 'Sem valor'}
            </Box>
            <Box sx={{ px: 1.1, height: 30, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 800 }}>
              {totalReach ? `${formatCompact(totalReach)} alcance total` : 'Sem alcance'}
            </Box>
          </Box>
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ display: 'grid', gap: 1, mt: 1.2 }}>
          {similarArticles.map(item => (
            <Box
              key={`similar-${item._id}`}
              onClick={() => onOpen(item)}
              sx={{
                display: 'grid',
                gridTemplateColumns: '44px minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: 1,
                px: 1.1,
                py: 0.9,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.045)',
                border: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer'
              }}
            >
              <Box
                component="img"
                src={getImageUrl(item)}
                alt=""
                onError={(event) => {
                  event.currentTarget.src = fallbackImage;
                }}
                sx={{ width: 44, height: 34, borderRadius: '7px', objectFit: 'cover' }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" translate="no" noWrap sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '0.78rem' }}>
                  {item.siteName || item.author || item.mediaType || 'Fonte desconhecida'}
                </Typography>
                <Typography variant="caption" translate="no" noWrap sx={{ display: 'block', color: 'rgba(248,250,252,0.48)' }}>
                  {item.title || 'Sem titulo'}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.56)', fontWeight: 800 }}>
                {formatCurrency(item.valuation) || '-'}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ClusterArticleModal = ({ cluster, allArticles = [], open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const clusterItems = React.useMemo(() => (Array.isArray(cluster) ? cluster : []), [cluster]);
  const [activeId, setActiveId] = React.useState(null);
  const [sideMode, setSideMode] = React.useState('article');
  const [contextMode, setContextMode] = React.useState('similar');
  const [fullArticle, setFullArticle] = React.useState(null);
  const [contentLoading, setContentLoading] = React.useState(false);

  React.useEffect(() => {
    setActiveId(clusterItems[0]?._id || null);
  }, [clusterItems]);

  const activeArticle = React.useMemo(
    () => allArticles.find(item => item._id === activeId) || clusterItems.find(item => item._id === activeId) || clusterItems[0] || null,
    [activeId, allArticles, clusterItems]
  );

  React.useEffect(() => {
    let cancelled = false;

    const fetchFullArticle = async () => {
      if (!open || !activeArticle?._id) {
        setFullArticle(null);
        return;
      }

      setFullArticle(activeArticle);
      setContentLoading(true);
      try {
        const response = await api.get(`/articles/${activeArticle._id}`);
        if (!cancelled) {
          setFullArticle(response.data?.data || activeArticle);
        }
      } catch (error) {
        if (!cancelled) {
          setFullArticle(activeArticle);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    };

    fetchFullArticle();

    return () => {
      cancelled = true;
    };
  }, [activeArticle, open]);

  if (!activeArticle) return null;

  const modalArticle = fullArticle || activeArticle;
  const imageUrl = getImageUrl(modalArticle);
  const source = modalArticle.siteName || modalArticle.author || modalArticle.mediaType || 'Fonte desconhecida';
  const originalUrl = modalArticle.siteUrl || modalArticle.url || modalArticle.videoUrl;
  const publicUrl = `${window.location.origin}/article/${modalArticle._id}`;
  const youtubeId = getModalYouTubeId(modalArticle);
  const videoUrl = getPlayableVideoUrl(modalArticle);
  const contentText = cleanArticleText(
    modalArticle.content || modalArticle.searchableContent || modalArticle.description || activeArticle.description
  );
  const activeTags = getArticleTags(activeArticle);
  const activeCollections = getArticleCollections(activeArticle);
  const tagItems = allArticles.filter(item => item._id !== activeArticle._id && getArticleTags(item).some(tag => activeTags.includes(tag)));
  const collectionItems = allArticles.filter(item => item._id !== activeArticle._id && getArticleCollections(item).some(collection => activeCollections.includes(collection)));
  const contextItems = contextMode === 'tags'
    ? [activeArticle, ...(tagItems.length ? tagItems : clusterItems.filter(item => item._id !== activeArticle._id))]
    : contextMode === 'collections'
      ? [activeArticle, ...(collectionItems.length ? collectionItems : clusterItems.filter(item => item._id !== activeArticle._id))]
      : clusterItems;
  const contextLabel = contextMode === 'tags'
    ? 'Tags'
    : contextMode === 'collections'
      ? 'Colecoes'
      : 'Similares';
  const totalValuation = contextItems.reduce((sum, item) => sum + Number(item.valuation || 0), 0);
  const totalReach = contextItems.reduce((sum, item) => {
    const reach = item.viewsNumber || item.audience || item.likesNumber || item.domainInfo?.estimatedAudience || 0;
    return sum + Number(reach || 0);
  }, 0);
  const articleReach = modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber || modalArticle.domainInfo?.estimatedAudience;
  const monthlyAudience = modalArticle.monthlyAudience || modalArticle.audienceMonthly || modalArticle.domainInfo?.monthlyVisits || modalArticle.domainInfo?.estimatedAudience;
  const dailyAudience = modalArticle.dailyAudience || modalArticle.audienceDaily || (monthlyAudience ? Math.round(Number(monthlyAudience) / 30) : null);
  const pageViews = modalArticle.pageViews || modalArticle.viewsNumber || modalArticle.views;
  const dailyPageViews = modalArticle.dailyPageViews || modalArticle.pageViewsDaily;
  const occupancy = modalArticle.editorialOccupancy || {};
  const sentiment = modalArticle.sentimentAnalysis?.sentiment || modalArticle.sentiment || modalArticle.sentimentLabel;
  const articleInfoSections = [
    {
      title: 'Dados da fonte',
      rows: [
        ['Tipo', modalArticle.mediaType || '-'],
        ['Abrangencia', modalArticle.coverage || modalArticle.scope || modalArticle.region || modalArticle.abrangencia || '-'],
        ['Fonte', source]
      ]
    },
    {
      title: 'Metricas',
      rows: [
        ['Audiencia mensal', monthlyAudience ? formatCompact(monthlyAudience) : '-'],
        ['Audiencia diaria', dailyAudience ? formatCompact(dailyAudience) : '-'],
        ['Page views', pageViews ? formatCompact(pageViews) : '-'],
        ['Page views diario', dailyPageViews ? formatCompact(dailyPageViews) : '-'],
        ['Valoracao', formatCurrency(modalArticle.valuation) || '-'],
        ['Alcance', articleReach ? formatCompact(articleReach) : '-']
      ]
    },
    {
      title: 'Mensuracao',
      rows: [
        ['Sentimento', sentiment || '-'],
        ['Tier', modalArticle.tier || modalArticle.sourceTier || '-'],
        ['Impacto', modalArticle.impact || modalArticle.impactLabel || '-'],
        ['Ocupacao Editorial', occupancy.valueCm ? `${occupancy.valueCm} cm` : (occupancy.valueCm2 ? `${occupancy.valueCm2} cm2` : '-')],
        ['Participacao', occupancy.brandParticipationPercent ? `${occupancy.brandParticipationPercent}%` : '-'],
        ['Area editorial', occupancy.editorialAreaTotalCm2 ? `${occupancy.editorialAreaTotalCm2} cm2` : '-'],
        ['Exposicao', modalArticle.exposure || occupancy.exposure || '-']
      ]
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 'min(1180px, calc(100vw - 36px))',
          height: fullScreen ? '100%' : 'min(88vh, 780px)',
          borderRadius: fullScreen ? 0 : '24px',
          overflow: 'hidden',
          bgcolor: '#20262d',
          boxShadow: '0 30px 86px rgba(0,0,0,0.48)',
          mt: 0
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          color: '#f8fafc',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.65fr) 360px' },
          gridTemplateRows: { xs: 'minmax(0, 1fr) auto', md: '1fr' },
          bgcolor: '#20262d',
          borderRadius: fullScreen ? 0 : '24px',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', minHeight: 0, overflowY: 'auto', overflowX: 'visible', scrollbarWidth: 'thin' }}>
          <Box
            component="img"
            src={imageUrl}
            alt=""
            aria-hidden="true"
            onError={(event) => {
              event.currentTarget.src = fallbackImage;
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.16,
              filter: 'blur(18px) brightness(0.62) saturate(0.84)',
              transform: 'scale(1.08)'
            }}
          />
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(32,38,45,0.50), rgba(32,38,45,0.94))' }} />

          <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 3.5 }, pt: { xs: 7, md: 5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
              <PlatformIcon mediaType={modalArticle.mediaType} />
              <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850 }}>
                {modalArticle.mediaType || 'Online'}
              </Typography>
            </Box>
            <Typography
              variant="h3"
              translate="no"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.48rem' },
                lineHeight: 1.08,
                fontWeight: 900,
                maxWidth: { xs: '100%', md: '86%' },
                mb: 2.2
              }}
            >
              {modalArticle.title || 'Sem titulo'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, maxWidth: { xs: '100%', md: '86%' }, mb: 2.2, minWidth: 0 }}>
              <Typography variant="body2" translate="no" noWrap sx={{ color: 'rgba(248,250,252,0.76)', fontWeight: 850, minWidth: 0, maxWidth: '60%' }}>
                {source}
              </Typography>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(248,250,252,0.30)', flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: 'rgba(248,250,252,0.56)', fontWeight: 800, flexShrink: 0 }}>
                {formatDate(modalArticle.date || modalArticle.createdAt)}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                width: { xs: '100%', md: '92%' },
                mx: { xs: 0, md: 'auto' },
                transform: { xs: 'none', md: 'translateY(-14px)' },
                zIndex: 3,
                mb: { xs: -2.4, md: -4.6 },
                aspectRatio: '16 / 9',
                borderRadius: '18px',
                overflow: 'hidden',
                bgcolor: '#121820',
                border: '1px solid rgba(255,255,255,0.07)',
                boxShadow: '0 28px 76px rgba(0,0,0,0.46)'
              }}
            >
              {youtubeId ? (
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={modalArticle.title || 'Video do YouTube'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : videoUrl ? (
                <Box component="video" src={videoUrl} controls playsInline poster={imageUrl} sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
              ) : (
                <Box component="img" src={imageUrl} alt={modalArticle.title || 'Artigo'} sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }} />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end'
                }}
              >
                <Box sx={{ px: 1.35, height: 34, borderRadius: 999, bgcolor: '#fff', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: 900 }}>
                  {formatCurrency(modalArticle.valuation) || 'Sem valor'}
                </Box>
                <Box sx={{ px: 1.1, height: 34, borderRadius: 999, bgcolor: 'rgba(32,38,45,0.78)', color: '#fff', display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: 800, backdropFilter: 'blur(12px)' }}>
                  {modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber ? `${formatCompact(modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber)} alcance` : 'Sem alcance'}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                borderRadius: '16px',
                bgcolor: 'rgba(88, 101, 116, 0.52)',
                backdropFilter: 'blur(20px) saturate(1.12)',
                WebkitBackdropFilter: 'blur(20px) saturate(1.12)',
                p: { xs: 2, md: 2.6 },
                pt: { xs: 4.2, md: 5.4 },
                boxShadow: '0 16px 44px rgba(0,0,0,0.22)'
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.82, lineHeight: 1.62, whiteSpace: 'pre-line', fontSize: { xs: '0.9rem', md: '0.94rem' } }}>
                {contentLoading ? 'Carregando conteudo...' : (contentText || 'Sem conteudo disponivel para este artigo.')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            minHeight: 0,
            overflowY: 'auto',
            px: 2,
            py: { xs: 2, md: 7 },
            bgcolor: '#242b33',
            borderLeft: { xs: 0, md: '1px solid rgba(255,255,255,0.06)' },
            scrollbarWidth: 'thin'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
            {sideMode === 'article' ? 'Materia' : 'Contexto'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.7, mb: 2, p: 0.35, borderRadius: 999, bgcolor: '#303842' }}>
            {[
              { value: 'article', label: 'Materia' },
              { value: 'context', label: 'Contexto' }
            ].map(item => {
              const selected = sideMode === item.value;
              return (
                <Box
                  key={item.value}
                  onClick={() => setSideMode(item.value)}
                  sx={{
                    flex: 1,
                    height: 34,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? '#f8fafc' : 'transparent',
                    color: selected ? '#10151c' : 'rgba(248,250,252,0.70)',
                    fontSize: '0.78rem',
                    fontWeight: 900,
                    cursor: 'pointer'
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
          {sideMode === 'context' ? (
            <>
          <Typography variant="body2" sx={{ opacity: 0.58, mb: 2 }}>
            {contextItems.length} publicacoes em {contextLabel.toLowerCase()}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.7, mb: 2, p: 0.35, borderRadius: 999, bgcolor: '#303842' }}>
            {[
              { value: 'similar', label: 'Similares', count: clusterItems.length },
              { value: 'tags', label: 'Tags', count: tagItems.length || activeTags.length },
              { value: 'collections', label: 'Colecoes', count: collectionItems.length || activeCollections.length }
            ].map(item => {
              const selected = contextMode === item.value;
              return (
                <Box
                  key={item.value}
                  onClick={() => setContextMode(item.value)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 32,
                    px: 1,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.45,
                    bgcolor: selected ? '#f8fafc' : 'transparent',
                    color: selected ? '#10151c' : 'rgba(248,250,252,0.70)',
                    fontSize: '0.74rem',
                    fontWeight: 900,
                    cursor: 'pointer'
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.label}
                  </Box>
                  <Box component="span" sx={{ opacity: selected ? 0.72 : 0.54 }}>
                    {item.count || 0}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {contextMode !== 'similar' && (
            <Box sx={{ display: 'flex', gap: 0.65, flexWrap: 'wrap', mb: 2 }}>
              {(contextMode === 'tags' ? activeTags : activeCollections).slice(0, 5).map(label => (
                <Box
                  key={`${contextMode}-${label}`}
                  translate="no"
                  sx={{
                    px: 1,
                    height: 26,
                    borderRadius: 999,
                    bgcolor: '#303842',
                    color: 'rgba(248,250,252,0.72)',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    maxWidth: '100%'
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label}
                  </Box>
                </Box>
              ))}
              {(contextMode === 'tags' ? activeTags : activeCollections).length === 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.48)' }}>
                  Sem {contextMode === 'tags' ? 'tags' : 'colecoes'} nessa materia.
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 2.2 }}>
            <Box sx={{ px: 1.15, height: 30, borderRadius: 999, bgcolor: '#fff', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 900 }}>
              {formatCurrency(totalValuation) || 'Sem valor'}
            </Box>
            <Box sx={{ px: 1.1, height: 30, borderRadius: 999, bgcolor: '#303842', display: 'flex', alignItems: 'center', fontSize: '0.78rem', fontWeight: 800 }}>
              {totalReach ? `${formatCompact(totalReach)} alcance total` : 'Sem alcance'}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            {contextItems.map(item => {
              const isActive = item._id === activeArticle._id;
              const itemReach = item.viewsNumber || item.audience || item.likesNumber || item.domainInfo?.estimatedAudience;
              return (
                <Box
                  key={`modal-cluster-${item._id}`}
                  onClick={() => setActiveId(item._id)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '52px minmax(0, 1fr)',
                    gap: 1,
                    p: 1,
                    borderRadius: '12px',
                    bgcolor: isActive ? '#404a55' : '#303842',
                    border: isActive ? '1px solid rgba(255,255,255,0.13)' : '1px solid rgba(255,255,255,0.05)',
                    cursor: 'pointer'
                  }}
                >
                  <Box
                    component="img"
                    src={getImageUrl(item)}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.src = fallbackImage;
                    }}
                    sx={{ width: 52, height: 48, borderRadius: '9px', objectFit: 'cover' }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850, color: '#fff' }}>
                      {item.siteName || item.author || item.mediaType || 'Fonte desconhecida'}
                    </Typography>
                    <Typography variant="caption" translate="no" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: 'rgba(248,250,252,0.58)', lineHeight: 1.25 }}>
                      {item.title || 'Sem titulo'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8, color: 'rgba(248,250,252,0.58)', fontSize: '0.72rem', fontWeight: 800 }}>
                      <Box component="span">{formatCurrency(item.valuation) || '-'}</Box>
                      <Box component="span">{itemReach ? formatCompact(itemReach) : '-'}</Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
            </>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {articleInfoSections.map(section => (
                <Box
                  key={section.title}
                  sx={{
                    borderRadius: '14px',
                    bgcolor: '#303842',
                    border: '1px solid rgba(255,255,255,0.05)',
                    overflow: 'hidden'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      px: 1.35,
                      pt: 1.2,
                      pb: 0.75,
                      color: 'rgba(248,250,252,0.58)',
                      fontWeight: 900,
                      textTransform: 'uppercase',
                      letterSpacing: 0
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box sx={{ display: 'grid' }}>
                    {section.rows.map(([label, value]) => (
                      <Box
                        key={`${section.title}-${label}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(100px, 0.8fr) minmax(0, 1fr)',
                          gap: 1,
                          px: 1.35,
                          py: 0.8,
                          borderTop: '1px solid rgba(255,255,255,0.05)'
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'rgba(248,250,252,0.52)', fontWeight: 800 }}>
                          {label}
                        </Typography>
                        <Typography variant="caption" translate="no" noWrap sx={{ color: '#f8fafc', fontWeight: 900, textAlign: 'right', minWidth: 0 }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2.4, position: { md: 'sticky' }, bottom: 0, py: 1.2, bgcolor: '#242b33' }}>
            <IconButton
              onClick={() => originalUrl && window.open(originalUrl, '_blank', 'noopener,noreferrer')}
              disabled={!originalUrl}
              sx={{ width: 42, height: 42, bgcolor: '#fff', color: '#10151c', '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              sx={{ width: 42, height: 42, bgcolor: '#303842', color: '#fff', '&:hover': { bgcolor: '#3a444f' } }}
            >
              <PublicIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            zIndex: 5,
            top: { xs: 12, md: 18 },
            right: { xs: 12, md: 18 },
            color: '#fff',
            bgcolor: 'rgba(0,0,0,0.36)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.52)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Dialog>
  );
};

const PrototypeArticleModal = ({ article, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [fullArticle, setFullArticle] = React.useState(null);
  const [contentLoading, setContentLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const fetchFullArticle = async () => {
      if (!open || !article?._id) {
        setFullArticle(null);
        return;
      }

      setFullArticle(article);
      setContentLoading(true);
      try {
        const response = await api.get(`/articles/${article._id}`);
        if (!cancelled) {
          setFullArticle(response.data?.data || article);
        }
      } catch (error) {
        if (!cancelled) {
          setFullArticle(article);
        }
      } finally {
        if (!cancelled) {
          setContentLoading(false);
        }
      }
    };

    fetchFullArticle();

    return () => {
      cancelled = true;
    };
  }, [article, open]);

  if (!article) return null;

  const modalArticle = fullArticle || article;
  const imageUrl = getImageUrl(modalArticle);
  const originalUrl = modalArticle.siteUrl || modalArticle.url || article.siteUrl || article.url;
  const publicUrl = `${window.location.origin}/article/${article._id}`;
  const source = modalArticle.siteName || modalArticle.author || modalArticle.mediaType || 'Fonte desconhecida';
  const valuation = formatCurrency(modalArticle.valuation);
  const reach = modalArticle.viewsNumber || modalArticle.audience || modalArticle.likesNumber || modalArticle.domainInfo?.estimatedAudience;
  const tagCount = modalArticle.tags?.length || 0;
  const youtubeId = getModalYouTubeId(modalArticle);
  const videoUrl = getPlayableVideoUrl(modalArticle);
  const hasHeroPlayer = Boolean(youtubeId || videoUrl);
  const contentText = cleanArticleText(
    modalArticle.content || modalArticle.searchableContent || modalArticle.description || article.description
  );
  const isSocialMedia = [
    'YouTube',
    'Instagram',
    'TikTok',
    'Facebook',
    'Twitter',
    'LinkedIn',
    'Threads',
    'Pinterest',
    'Kwai'
  ].includes(modalArticle.mediaType);
  const isMediaFirst = isSocialMedia || hasHeroPlayer;

  if (isMediaFirst) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: fullScreen ? '100%' : 'min(1120px, calc(100vw - 36px))',
            height: fullScreen ? '100%' : 'min(86vh, 760px)',
            borderRadius: fullScreen ? 0 : '24px',
            overflow: 'hidden',
            bgcolor: '#070b12',
            boxShadow: '0 34px 100px rgba(0,0,0,0.55)'
          }
        }}
      >
        <Box
          sx={{
            height: '100%',
            color: '#f8fafc',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.85fr) minmax(340px, 0.9fr)' },
            gridTemplateRows: { xs: 'minmax(260px, 48vh) minmax(0, 1fr)', md: '1fr' },
            bgcolor: '#070b12'
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: 0,
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              bgcolor: '#030712'
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt=""
              aria-hidden="true"
              onError={(event) => {
                event.currentTarget.src = fallbackImage;
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.18,
                filter: 'blur(18px) brightness(0.55)',
                transform: 'scale(1.08)'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 50% 45%, rgba(37,99,235,0.16), transparent 48%), linear-gradient(180deg, rgba(3,7,18,0.20), rgba(3,7,18,0.78))'
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                top: 18,
                left: 20,
                right: 70,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#fff',
                minWidth: 0,
                '& .MuiSvgIcon-root': { fontSize: 20 }
              }}
            >
              <PlatformIcon mediaType={modalArticle.mediaType} />
              <Typography variant="body2" translate="no" noWrap sx={{ fontWeight: 850 }}>
                {modalArticle.mediaType || 'Online'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.55 }}>-</Typography>
              <Typography variant="body2" translate="no" noWrap sx={{ opacity: 0.72, fontWeight: 700 }}>
                {source}
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                p: { xs: 1.5, md: 4 },
                pt: { xs: 6.5, md: 7.5 }
              }}
            >
              {youtubeId ? (
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={modalArticle.title || 'Video do YouTube'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{
                    width: '100%',
                    maxWidth: 860,
                    aspectRatio: '16 / 9',
                    border: 0,
                    borderRadius: { xs: '14px', md: '18px' },
                    bgcolor: '#000',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.46)'
                  }}
                />
              ) : videoUrl ? (
                <Box
                  component="video"
                  src={videoUrl}
                  controls
                  playsInline
                  poster={imageUrl}
                  sx={{
                    width: '100%',
                    maxWidth: 860,
                    maxHeight: '100%',
                    borderRadius: { xs: '14px', md: '18px' },
                    objectFit: 'contain',
                    bgcolor: '#000',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.46)'
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={modalArticle.title || 'Artigo'}
                  onError={(event) => {
                    event.currentTarget.src = fallbackImage;
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 620,
                    objectFit: 'contain',
                    borderRadius: { xs: '14px', md: '18px' },
                    filter: 'none',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.34)'
                  }}
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              minHeight: 0,
              overflowY: 'auto',
              px: { xs: 2.2, md: 3.2 },
              pt: { xs: 2.4, md: 7.5 },
              pb: { xs: 2.5, md: 3.2 },
              bgcolor: 'rgba(13, 19, 30, 0.96)',
              borderLeft: { xs: 0, md: '1px solid rgba(255,255,255,0.08)' },
              scrollbarWidth: 'thin'
            }}
          >
            <Typography variant="caption" translate="no" sx={{ display: 'block', opacity: 0.58, fontWeight: 850, mb: 1 }}>
              {formatDate(modalArticle.date || modalArticle.createdAt)}
            </Typography>

            <Typography
              variant="h4"
              translate="no"
              sx={{
                fontSize: { xs: '1.35rem', md: '1.72rem' },
                lineHeight: 1.08,
                fontWeight: 900,
                mb: 1.4
              }}
            >
              {modalArticle.title || 'Sem titulo'}
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.68, lineHeight: 1.5, mb: 2 }}>
              {modalArticle.description || modalArticle.author || 'Sem resumo disponivel para este artigo.'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mb: 2.4 }}>
              <Box sx={{ px: 1.35, height: 34, borderRadius: 999, bgcolor: '#f8fafc', color: '#10151c', display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: 900 }}>
                {valuation || 'Sem valor'}
              </Box>
              <Box sx={{ px: 1.1, height: 34, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', fontSize: '0.8rem', fontWeight: 800 }}>
                {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
              </Box>
              <Box sx={{ px: 1.1, height: 34, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', gap: 0.65, fontSize: '0.8rem', fontWeight: 800 }}>
                <LabelIcon sx={{ fontSize: 16 }} />
                {tagCount ? `${tagCount} tags` : 'Sem tags'}
              </Box>
            </Box>

            <Typography
              variant="body2"
              sx={{
                opacity: 0.82,
                lineHeight: 1.68,
                whiteSpace: 'pre-line',
                fontSize: { xs: '0.9rem', md: '0.94rem' }
              }}
            >
              {contentLoading ? 'Carregando conteudo...' : (contentText || 'Sem conteudo disponivel para este artigo.')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mt: 3, position: { md: 'sticky' }, bottom: 0, py: 1.2, bgcolor: 'rgba(13, 19, 30, 0.92)' }}>
              <IconButton
                onClick={() => originalUrl && window.open(originalUrl, '_blank', 'noopener,noreferrer')}
                disabled={!originalUrl}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: '#fff',
                  color: '#10151c',
                  '&:hover': { bgcolor: '#f1f5f9' },
                  '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.16)', color: 'rgba(255,255,255,0.38)' }
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: 'rgba(255,255,255,0.10)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.17)' }
                }}
              >
                <PublicIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              zIndex: 5,
              top: { xs: 12, md: 18 },
              right: { xs: 12, md: 18 },
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.10)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      PaperProps={{
        sx: {
          width: fullScreen ? '100%' : 760,
          maxWidth: 'calc(100vw - 32px)',
          height: fullScreen ? '100%' : 'min(86vh, 720px)',
          borderRadius: fullScreen ? 0 : '28px',
          overflow: 'hidden',
          bgcolor: '#07111f',
          boxShadow: '0 30px 90px rgba(0,0,0,0.45)'
        }
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', color: '#fff' }}>
        <Box
          component="img"
          src={imageUrl}
          alt={article.title || 'Artigo'}
          onError={(event) => {
            event.currentTarget.src = fallbackImage;
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: hasHeroPlayer ? 'brightness(0.42) blur(10px)' : 'brightness(0.76)',
            transform: hasHeroPlayer ? 'scale(1.04)' : 'none'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: hasHeroPlayer
              ? 'linear-gradient(180deg, rgba(5,10,18,0.72) 0%, rgba(5,10,18,0.28) 42%, rgba(5,10,18,0.82) 100%)'
              : 'linear-gradient(180deg, rgba(5,10,18,0.48) 0%, rgba(5,10,18,0.20) 38%, rgba(5,10,18,0.78) 100%)'
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 22,
            left: 24,
            right: 88,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 850,
            '& .MuiSvgIcon-root': { fontSize: 20 }
          }}
        >
          <PlatformIcon mediaType={article.mediaType} />
          <Typography variant="body2" translate="no" sx={{ fontWeight: 850 }}>
            {modalArticle.mediaType || 'Online'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>•</Typography>
          <Typography variant="body2" translate="no" noWrap sx={{ opacity: 0.82, fontWeight: 700 }}>
            {source}
          </Typography>
        </Box>

        {hasHeroPlayer && (
          <Box
            sx={{
              position: 'absolute',
              zIndex: 2,
              top: { xs: 68, md: 70 },
              left: { xs: 14, md: 34 },
              right: { xs: 14, md: 34 },
              mx: 'auto',
              maxWidth: 680,
              aspectRatio: '16 / 9',
              borderRadius: { xs: '18px', md: '22px' },
              overflow: 'hidden',
              bgcolor: '#030712',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: '0 22px 70px rgba(0,0,0,0.42)'
            }}
          >
            {youtubeId ? (
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                title={modalArticle.title || 'Video do YouTube'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  display: 'block'
                }}
              />
            ) : (
              <Box
                component="video"
                src={videoUrl}
                controls
                playsInline
                poster={imageUrl}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  bgcolor: '#030712'
                }}
              />
            )}
          </Box>
        )}

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            zIndex: 4,
            top: 16,
            right: 18,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.10)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            px: { xs: 2.2, md: 4 },
            pt: { xs: 3, md: 4 },
            pb: { xs: 2.4, md: 3.2 },
            maxHeight: fullScreen ? '76vh' : '68%',
            ...(hasHeroPlayer && {
              maxHeight: fullScreen ? '52vh' : '42%'
            }),
            overflowY: 'auto',
            borderRadius: fullScreen ? '22px 22px 0 0' : '28px 28px 0 0',
            zIndex: 3,
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(145, 164, 184, 0.42)'
              : 'rgba(211, 225, 238, 0.50)',
            color: theme.palette.mode === 'dark' ? '#f6f8fb' : '#17202b',
            boxShadow: '0 -24px 72px rgba(3, 8, 18, 0.22)',
            backdropFilter: 'blur(24px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
            scrollbarWidth: 'thin',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 72,
              height: 4,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.56)'
            }
          }}
        >
          <Typography variant="caption" translate="no" sx={{ display: 'block', opacity: 0.74, fontWeight: 850, mb: 1 }}>
            {formatDate(modalArticle.date || modalArticle.createdAt)}
          </Typography>

          <Typography
            variant="h3"
            translate="no"
            sx={{
              fontSize: { xs: '1.55rem', md: '2.2rem' },
              lineHeight: 1.05,
              fontWeight: 900,
              maxWidth: 680,
              mb: 1.5
            }}
          >
            {modalArticle.title || 'Sem titulo'}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: 620,
              opacity: 0.76,
              lineHeight: 1.45,
              mb: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {modalArticle.description || modalArticle.author || 'Sem resumo disponivel para este artigo.'}
          </Typography>

          <Box
            sx={{
              maxWidth: 660,
              mb: 2.4
            }}
          >
            <Typography
              variant="body2"
              sx={{
                opacity: 0.84,
                lineHeight: 1.62,
                whiteSpace: 'pre-line',
                fontSize: { xs: '0.9rem', md: '0.96rem' }
              }}
            >
              {contentLoading ? 'Carregando conteudo...' : (contentText || 'Sem conteudo disponivel para este artigo.')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box
              sx={{
                px: 1.5,
                height: 36,
                borderRadius: 999,
                bgcolor: theme.palette.mode === 'dark' ? '#f8fafc' : '#fff',
                color: '#10151c',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.82rem',
                fontWeight: 900
              }}
            >
              {valuation || 'Sem valor'}
            </Box>
            <Box sx={{ px: 1.2, height: 36, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', fontWeight: 800 }}>
              {reach ? `${formatCompact(reach)} alcance` : 'Sem alcance'}
            </Box>
            <Box sx={{ px: 1.2, height: 36, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', gap: 0.7, fontWeight: 800 }}>
              <LabelIcon sx={{ fontSize: 17 }} />
              {tagCount ? `${tagCount} tags` : 'Sem tags'}
            </Box>
            <Box sx={{ flex: 1 }} />
            <IconButton
              onClick={() => originalUrl && window.open(originalUrl, '_blank', 'noopener,noreferrer')}
              disabled={!originalUrl}
              sx={{
                width: 42,
                height: 42,
                bgcolor: '#fff',
                color: '#10151c',
                '&:hover': { bgcolor: '#f1f5f9' },
                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.28)', color: 'rgba(16,21,28,0.45)' }
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              sx={{
                width: 42,
                height: 42,
                bgcolor: 'rgba(255,255,255,0.18)',
                color: 'inherit',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }
              }}
            >
              <PublicIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

// ─── Profile-Style Article Card ────────────────────────────────────────────
const ProfileStyleArticleCard = ({ article, onOpen }) => {
  const [copied, setCopied] = React.useState(false);
  const imageUrl = getImageUrl(article);
  const source = article.siteName || article.author || article.mediaType || 'Fonte desconhecida';
  const originalUrl = article.siteUrl || article.url;
  const isOnline = article.status !== 'offline';
  const glowText = article.mediaType
    ? `Conteudo em ${article.mediaType}`
    : 'Currently High on Creativity';

  const dateText = React.useMemo(() => {
    const d = article.date || article.createdAt;
    if (!d) return 'Sem data';
    const dt = new Date(d);
    const h = dt.getHours();
    const m = dt.getMinutes().toString().padStart(2, '0');
    const hour12 = ((h + 11) % 12) + 1;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m}${ampm}`;
  }, [article]);

  const handleCopyLink = (event) => {
    event.stopPropagation();
    const link = originalUrl || window.location.href;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  const handleOpen = (event) => {
    event.stopPropagation();
    if (originalUrl) window.open(originalUrl, '_blank', 'noopener,noreferrer');
  };

  const actionSx = {
    height: 54,
    borderRadius: '16px',
    bgcolor: 'rgba(255,255,255,0.10)',
    color: '#fff',
    border: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 1.25,
    px: 2,
    fontSize: '0.92rem',
    fontWeight: 850,
    cursor: 'pointer',
    transition: 'background-color 150ms ease, transform 150ms ease',
    minWidth: 0,
    '& .MuiButton-startIcon': {
      mr: 0,
      ml: 0
    },
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.16)',
      transform: 'translateY(-1px)'
    }
  };

  return (
    <Box
      onClick={() => onOpen(article)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        width: '100%',
        maxWidth: 760,
        mx: 'auto',
        pb: 5.8,
        '@keyframes profileCardIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        },
        animation: 'profileCardIn 0.4s ease-out both',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          top: '70%',
          zIndex: 0,
          borderRadius: '28px',
          bgcolor: '#84cc16',
          boxShadow: '0 42px 90px -18px rgba(132,204,22,0.78)',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          height: 46,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
          color: '#080b0d',
          fontSize: '0.9rem',
          fontWeight: 800,
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <BoltIcon sx={{ fontSize: 18, flexShrink: 0 }} />
        <Box component="span" translate="no" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {glowText}
        </Box>
      </Box>

      {/* Card body escuro — zIndex 1 fica acima da prateleira verde */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          borderRadius: '28px',
          border: 0,
          background: 'radial-gradient(120% 120% at 30% 10%, #1a1a1a 0%, #0f0f10 60%, #0b0b0c 100%)',
          color: '#fff',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          p: { xs: 3, sm: 4 },
        }}
      >
        {/* Top row: status + date */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 4, fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: 0 }}>
            <Box
              sx={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                bgcolor: isOnline ? '#84cc16' : '#6b7280',
                flexShrink: 0,
                '@keyframes pulseGreen': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 }
                },
                animation: isOnline ? 'pulseGreen 1.8s ease-in-out infinite' : 'none',
              }}
            />
            <Box component="span" sx={{ fontWeight: 600, color: '#fff', userSelect: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {isOnline ? 'Online' : 'Offline'}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, opacity: 0.76, flexShrink: 0 }}>
            <AccessTimeIcon sx={{ fontSize: 18 }} />
            <Box component="span" sx={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
              {dateText}
            </Box>
          </Box>
        </Box>

        {/* Avatar + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 4, minWidth: 0 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.12)',
              flexShrink: 0,
              bgcolor: '#1e2533',
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={source}
              onError={(e) => { e.currentTarget.src = fallbackImage; }}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              translate="no"
              sx={{
                fontSize: { xs: '1.18rem', sm: '1.45rem' },
                fontWeight: 720,
                lineHeight: 1.12,
                letterSpacing: 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mb: 0.5
              }}
            >
              {article.title || 'Sem título'}
            </Typography>
            <Typography
              translate="no"
              noWrap
              sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.68)', fontWeight: 500 }}
            >
              {source}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Button
            type="button"
            onClick={handleOpen}
            startIcon={<OpenInNewIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
            sx={actionSx}
          >
            Abrir artigo
          </Button>
          <Button
            type="button"
            onClick={handleCopyLink}
            startIcon={<ContentCopyIcon sx={{ fontSize: 18, flexShrink: 0 }} />}
            sx={actionSx}
          >
            {copied ? 'Copiado!' : 'Copiar link'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const CardsTestingPage = () => {
  const { isAdminOrMensure, activeBrandData } = useBrand();
  const [selectedArticle, setSelectedArticle] = React.useState(null);
  const [selectedLightEditorialArticle, setSelectedLightEditorialArticle] = React.useState(null);
  const [selectedEditorialArticle, setSelectedEditorialArticle] = React.useState(null);
  const [selectedCluster, setSelectedCluster] = React.useState(null);
  const { data, isLoading, error } = useArticlesQuery({
    page: 1,
    limit: 24,
    sort: 'date_desc',
    enabled: isAdminOrMensure
  });

  if (!isAdminOrMensure) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Acesso restrito a administradores.</Alert>
      </Box>
    );
  }

  const articles = data?.data || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
          Cerds testando
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Protótipo visual de card leve usando artigos reais{activeBrandData?.name ? ` de ${activeBrandData.name}` : ''}.
        </Typography>
      </Box>

      <ToolbarSearchConcept
        articles={articles}
        brandName={activeBrandData?.name}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'Erro ao carregar artigos.'}
        </Alert>
      )}

      {!isLoading && articles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <VisualFilterExplorer
            articles={articles}
            brandName={activeBrandData?.name}
          />
        </Box>
      )}

      {!isLoading && articles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <LiveResultsReport
            articles={articles}
            title="Relatório vivo: recorte visual"
            brandName={activeBrandData?.name}
          />
        </Box>
      )}

      {/* ── Profile Style Card ── */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 0.5 }}>
          Profile style
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Card escuro com status pulsante, hora da publicação, avatar da fonte e dois botões de ação. Glow verde lime na base.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(520px, 1fr))' },
            gap: { xs: 4.5, md: 5 },
            pb: 2
          }}
        >
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
              <Box key={`ps-skel-${i}`} sx={{ pt: 0 }}>
                <Skeleton variant="rounded" height={312} sx={{ borderRadius: '28px', bgcolor: 'rgba(255,255,255,0.06)' }} />
              </Box>
            ))
            : articles.slice(0, 8).map(article => (
              <ProfileStyleArticleCard
                article={article}
                onOpen={setSelectedArticle}
                key={`ps-${article._id}`}
              />
            ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Editorial claro
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
            gap: 2
          }}
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="rounded" height={438} sx={{ borderRadius: '20px' }} key={`light-skeleton-${index}`} />
            ))
            : articles.slice(0, 6).map(article => (
              <EditorialLightArticleCard article={article} onOpen={setSelectedLightEditorialArticle} key={`light-${article._id}`} />
            ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Editorial escuro
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
            gap: 2
          }}
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="rounded" height={438} sx={{ borderRadius: '20px' }} key={`dark-skeleton-${index}`} />
            ))
            : articles.slice(0, 6).map(article => (
              <EditorialDarkArticleCard article={article} onOpen={setSelectedEditorialArticle} key={`dark-${article._id}`} />
            ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Editorial lista claro
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="rounded" height={172} sx={{ borderRadius: '20px' }} key={`light-list-skeleton-${index}`} />
            ))
            : articles.slice(0, 6).map(article => (
              <EditorialListArticleRow
                article={article}
                onOpen={setSelectedLightEditorialArticle}
                variant="light"
                key={`light-list-${article._id}`}
              />
            ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Editorial lista escuro
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="rounded" height={172} sx={{ borderRadius: '20px' }} key={`dark-list-skeleton-${index}`} />
            ))
            : articles.slice(0, 6).map(article => (
              <EditorialListArticleRow
                article={article}
                onOpen={setSelectedEditorialArticle}
                variant="dark"
                key={`dark-list-${article._id}`}
              />
            ))}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Hibrido: glass + leitura social
        </Typography>
        <Grid container spacing={2}>
          {isLoading
            ? Array.from({ length: 7 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={12 / 7} key={`hybrid-skeleton-${index}`}>
                <Skeleton variant="rounded" height={386} sx={{ borderRadius: '8px' }} />
              </Grid>
            ))
            : articles.slice(0, 7).map(article => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} xl={12 / 7} key={`hybrid-${article._id}`}>
                <HybridArticleCard article={article} onOpen={setSelectedArticle} />
              </Grid>
            ))}
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Agrupado por noticia similar
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 2
          }}
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton variant="rounded" height={420} sx={{ borderRadius: '14px' }} key={`cluster-skeleton-${index}`} />
            ))
            : [0, 3, 6, 9, 12, 15].map((start) => {
              const group = articles.slice(start, start + 6);
              if (!group.length) return null;
              return (
                <SimilarClusterCard
                  articles={group}
                  onOpen={setSelectedArticle}
                  onOpenCluster={setSelectedCluster}
                  key={`cluster-${start}`}
                />
              );
            })}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
          Hibrido empilhado
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {isLoading
            ? Array.from({ length: 3 }).map((_, index) => (
              <Skeleton variant="rounded" height={226} sx={{ borderRadius: '12px' }} key={`stacked-skeleton-${index}`} />
            ))
            : articles.slice(0, 3).map(article => (
              <StackedHybridArticleCard article={article} onOpen={setSelectedArticle} key={`stacked-${article._id}`} />
            ))}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 850, mb: 1.5 }}>
        Glass compacto atual
      </Typography>
      <Grid container spacing={2}>
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} xl={12 / 7} key={index}>
              <Skeleton variant="rounded" height={368} sx={{ borderRadius: '18px' }} />
            </Grid>
          ))
          : articles.map(article => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} xl={12 / 7} key={article._id}>
              <PrototypeArticleCard article={article} onOpen={setSelectedArticle} />
            </Grid>
          ))}
      </Grid>

      {!isLoading && articles.length === 0 && (
        <Alert severity="info">Nenhum artigo encontrado para testar os cards.</Alert>
      )}

      <PrototypeArticleModal
        article={selectedArticle}
        open={Boolean(selectedArticle)}
        onClose={() => setSelectedArticle(null)}
      />
      <EditorialLightArticleModal
        article={selectedLightEditorialArticle}
        open={Boolean(selectedLightEditorialArticle)}
        onClose={() => setSelectedLightEditorialArticle(null)}
      />
      <EditorialDarkArticleModal
        article={selectedEditorialArticle}
        open={Boolean(selectedEditorialArticle)}
        onClose={() => setSelectedEditorialArticle(null)}
      />
      <ClusterArticleModal
        cluster={selectedCluster}
        allArticles={articles}
        open={Boolean(selectedCluster)}
        onClose={() => setSelectedCluster(null)}
      />
    </Box>
  );
};

export default CardsTestingPage;
